import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { dashboardApi, kpisApi, risksApi, projectsApi, reportsApi, divisionsApi, objectivesApi, reportTemplatesApi, grantsApi } from '../services/api';
import {
  PieChart, Plus, Trash2, Edit3, X, ChevronDown, CheckCircle2, Clock, Circle,
  Download, FileSpreadsheet, AlertTriangle, Filter, Target, TrendingUp, TrendingDown,
  BarChart3, ShieldCheck, FolderKanban, Printer, FileText, Save, Play, Settings2,
  Sparkles, Activity, Layers, Lock, Share2, Copy, Wand2
} from 'lucide-react';
import { exportPdf, pdfStatCard, pdfTable, pdfSection, pdfBadge, pdfProgress } from '../utils/MintekPdfExport';
import { exportWord } from '../utils/MintekWordExport';
import {
  projectKpiAnnual, projectBudget, projectCompletion, riskTrendDirection
} from '../utils/Forecasting';

const fmtR = (v) => v != null ? (v >= 1000000 ? `R${(v/1000000).toFixed(1)}M` : v >= 1000 ? `R${(v/1000).toFixed(0)}K` : `R${v}`) : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-ZA',{day:'numeric',month:'short',year:'numeric'}) : '—';
const pc = (p) => p >= 70 ? '#10b981' : p >= 40 ? '#f59e0b' : '#ef4444';
const varColor = (v) => v==null?'text-slate-400':v>=0?'text-emerald-600':v>=-15?'text-amber-600':'text-red-600';
const varBg = (v) => v==null?'bg-slate-100':v>=0?'bg-emerald-100':v>=-15?'bg-amber-100':'bg-red-100';
const rlc = (s) => s<=4?'bg-emerald-100 text-emerald-700':s<=8?'bg-blue-100 text-blue-700':s<=12?'bg-amber-100 text-amber-700':'bg-red-100 text-red-700';
const rlBg = (s) => s<=4?'bg-emerald-100':s<=8?'bg-yellow-100':s<=12?'bg-amber-200':s<=16?'bg-orange-300':'bg-red-400';
const approvalBadge = (s) => ({Approved:'bg-emerald-100 text-emerald-700',Pending:'bg-amber-100 text-amber-700',Rejected:'bg-red-100 text-red-700'})[s]||'bg-slate-100 text-slate-600';
const QS = ['Q1','Q2','Q3','Q4'];
const input = 'w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#c8102e]/20 focus:border-[#c8102e] bg-white';
const btnP = 'px-4 py-2 bg-[#c8102e] text-white rounded-xl text-xs font-semibold hover:bg-[#a00d24] transition-colors flex items-center gap-1.5';
const btnS = 'px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50 transition-colors flex items-center gap-1.5';
const softPanel = 'bg-white/90 rounded-[22px] border border-slate-200 shadow-[0_8px_24px_rgba(15,23,42,0.08)]';
const softTitle = 'text-[11px] font-bold uppercase tracking-[0.08em] text-slate-700';
const burgundyBtn = 'px-4 py-2 bg-[#8f1024] text-white rounded-xl text-xs font-semibold hover:bg-[#760d1d] transition-colors flex items-center gap-1.5 shadow-[0_8px_18px_rgba(143,16,36,0.25)]';
const glassPanel = softPanel;
const panelHeader = softTitle;
const pdfBtn = burgundyBtn;

// V3.1 heatmap palette — graduated red used in Enterprise Risk view.
// Lighter cells = lower residual score; darker cells = critical risk. Matches the screenshot precisely.
const heatColor = (s) => {
  if (s <= 3)  return { bg: 'bg-red-50',   text: 'text-slate-500',  label: '' };
  if (s <= 6)  return { bg: 'bg-red-100',  text: 'text-slate-700',  label: 'low' };
  if (s <= 9)  return { bg: 'bg-red-200',  text: 'text-slate-800',  label: 'medium' };
  if (s <= 12) return { bg: 'bg-red-300',  text: 'text-slate-900',  label: 'medium' };
  if (s <= 16) return { bg: 'bg-red-400',  text: 'text-white',      label: 'high' };
  if (s <= 20) return { bg: 'bg-red-600',  text: 'text-white',      label: 'high' };
                return { bg: 'bg-red-800',  text: 'text-white',      label: 'critical' };
};

function Modal({open,onClose,title,wide,children}){if(!open)return null;return(<div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}><div className="absolute inset-0 bg-black/40 backdrop-blur-sm"/><div className={`relative bg-white rounded-2xl shadow-2xl ${wide?'max-w-5xl':'max-w-lg'} w-full max-h-[90vh] overflow-y-auto`} onClick={e=>e.stopPropagation()}><div className="sticky top-0 bg-white rounded-t-2xl border-b px-6 py-4 flex items-center justify-between z-10"><h2 className="font-bold text-lg text-slate-800">{title}</h2><button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 text-lg">×</button></div><div className="p-6">{children}</div></div></div>);}

// ── CSV/Excel export helper ──
function exportCSV(headers, rows, filename) {
  const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${(c??'').toString().replace(/"/g,'""')}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
}

// ── HTML-Excel exporter ──
// Wraps a table in the SpreadsheetML XML envelope so MS Excel / Google Sheets /
// LibreOffice Calc all open it as a native spreadsheet with proper column widths
// and basic styling. Filename uses .xls; mime is application/vnd.ms-excel.
// Used by the Custom Report Builder when the user picks "Excel" as the format.
function exportExcel(title, headers, rows, filename) {
  const thead = headers.map(h => `<th style="background:#f1f5f9;font-weight:700;border:1px solid #cbd5e1;padding:6px;text-align:left">${h}</th>`).join('');
  const tbody = rows.map(r =>
    `<tr>${r.map(c => `<td style="border:1px solid #e2e8f0;padding:4px 6px">${c ?? ''}</td>`).join('')}</tr>`
  ).join('');
  const html = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:x='urn:schemas-microsoft-com:office:excel' xmlns='http://www.w3.org/TR/REC-html40'>
    <head><meta charset="utf-8"/><title>${title}</title>
      <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
        <x:Name>${title.slice(0,30)}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
      </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
    </head>
    <body><h2 style="font-family:Calibri,sans-serif;color:#c8102e">${title}</h2>
      <table border="1" style="border-collapse:collapse;font-family:Calibri,sans-serif;font-size:11pt">
        <thead><tr>${thead}</tr></thead><tbody>${tbody}</tbody>
      </table>
    </body></html>`;
  const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename.endsWith('.xls') || filename.endsWith('.xlsx') ? filename : `${filename}.xls`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

// Donut chart used in Portfolio Health → Budget Utilisation (Image 5).
// Pure SVG so no extra deps and it renders identically server-side & in PDF.
function DonutChart({ percent, size = 160, strokeWidth = 22, label = '' }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.min(Math.max(percent, 0), 100) / 100) * circumference;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#c8102e" strokeWidth={strokeWidth}
          strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-black text-slate-800">{percent.toFixed(1)}%</span>
        {label && <span className="text-[10px] text-slate-400 mt-0.5">{label}</span>}
      </div>
    </div>
  );
}

export default function ReportingAnalyticsPage() {
  const [view,setView]=useState('executive'); // executive | risk | performance | portfolio | forecasting | reports | workflows
  const [fy,setFy]=useState(2026); const [selQ,setSelQ]=useState('Q1');
  const [dashboard,setDashboard]=useState(null); const [kpis,setKpis]=useState([]);
  const [risks,setRisks]=useState([]); const [projects,setProjects]=useState([]);
  const [divisions,setDivisions]=useState([]); const [objectives,setObjectives]=useState([]);
  const [reports,setReports]=useState([]); const [modal,setModal]=useState(null);
  const [loading,setLoading]=useState(true);

  // V3.2 — Custom Report Builder state
  const [templates, setTemplates] = useState([]);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [draftTemplate, setDraftTemplate] = useState(null);

  // V3.4 — Grants available as a data source in the Custom Report Builder
  const [grants, setGrants] = useState([]);

  // Open the builder with either an existing template or a blank draft
  const openBuilder = (template = null) => {
    if (template) {
      let cfg = {};
      try { cfg = typeof template.configurationJson === 'string' ? JSON.parse(template.configurationJson) : (template.configurationJson || {}); } catch {}
      setEditingTemplate(template);
      setDraftTemplate({
        id: template.id,
        name: template.name || '',
        description: template.description || '',
        dataSource: template.dataSource || 'KPIs',
        outputFormat: template.outputFormat || 'PDF',
        isShared: !!template.isShared,
        fields: cfg.fields || [],
        filters: cfg.filters || {},
        groupBy: cfg.groupBy || '',
        sortBy: cfg.sortBy || '',
        sortOrder: cfg.sortOrder || 'asc',
        includeCoverPage: cfg.includeCoverPage !== false,
        includeExecSummary: cfg.includeExecSummary !== false
      });
    } else {
      setEditingTemplate(null);
      setDraftTemplate({
        name: '', description: '',
        dataSource: 'KPIs', outputFormat: 'PDF', isShared: false,
        fields: ['reference','name'], filters: {}, groupBy: '',
        sortBy: 'reference', sortOrder: 'asc',
        includeCoverPage: true, includeExecSummary: true
      });
    }
    setBuilderOpen(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes,kRes,rRes,pRes,divRes,soRes,repRes,tplRes,gRes] = await Promise.allSettled([
        dashboardApi.get(fy), kpisApi.getAll({financialYear:fy}), risksApi.getAll({}),
        projectsApi.getAll({}), divisionsApi.getAll(), objectivesApi.getAll(fy),
        reportsApi.getAll(fy), reportTemplatesApi.getAll(),
        grantsApi.getAll()
      ]);
      if(dRes.status==='fulfilled') setDashboard(dRes.value.data);
      if(kRes.status==='fulfilled') setKpis(kRes.value.data||[]);
      if(rRes.status==='fulfilled') setRisks(rRes.value.data||[]);
      if(pRes.status==='fulfilled') setProjects(pRes.value.data||[]);
      if(divRes.status==='fulfilled') setDivisions(divRes.value.data||[]);
      if(soRes.status==='fulfilled') setObjectives(soRes.value.data||[]);
      if(repRes.status==='fulfilled') setReports(repRes.value.data||[]);
      if(tplRes.status==='fulfilled') setTemplates(tplRes.value.data||[]);
      if(gRes.status==='fulfilled') setGrants(gRes.value.data||[]);
    } catch {}
    setLoading(false);
  }, [fy]);
  useEffect(() => { load(); }, [load]);

  // Computed
  const appKpis = kpis.filter(k=>k.isAppIndicator);
  const activeRisks = risks.filter(r=>!r.isOpportunity&&r.status!=='Closed');
  const activeProjects = projects.filter(p=>p.status==='Active'||p.status==='InProgress');
  const getQActual = (k,q) => (k.actuals||[]).find(a=>a.quarter===q&&a.financialYear===fy);
  const getQTarget = (k,q) => ({Q1:k.q1Target,Q2:k.q2Target,Q3:k.q3Target,Q4:k.q4Target})[q];

  // Generate report
  const generateReport = async (reportType, quarter) => {
    try {
      const qLabel = quarter || 'Annual';
      await reportsApi.create({ title:`${reportType} Report — ${qLabel} FY${fy}/${(fy+1)%100}`, reportType, financialYear:fy, quarter: quarter ? QS.indexOf(quarter)+1 : null, status:'Draft' });
      load();
    } catch(e) { alert('Error: '+(e.response?.data?.title||e.message)); }
  };

  // Export functions
  const exportKpiReport = () => {
    const headers = ['Reference','KPI','Type','Division','Annual Target','Q1 Target','Q1 Actual','Q1 Var%','Q2 Target','Q2 Actual','Q2 Var%','Q3 Target','Q3 Actual','Q3 Var%','Q4 Target','Q4 Actual','Q4 Var%'];
    const rows = appKpis.map(k => [k.reference, k.name, k.isAppIndicator?'APP':'OPS', k.division?.name||'—', k.annualTarget,
      k.q1Target, getQActual(k,'Q1')?.actualValue, getQActual(k,'Q1')?.variancePercentage?.toFixed(1),
      k.q2Target, getQActual(k,'Q2')?.actualValue, getQActual(k,'Q2')?.variancePercentage?.toFixed(1),
      k.q3Target, getQActual(k,'Q3')?.actualValue, getQActual(k,'Q3')?.variancePercentage?.toFixed(1),
      k.q4Target, getQActual(k,'Q4')?.actualValue, getQActual(k,'Q4')?.variancePercentage?.toFixed(1)]);
    exportCSV(headers, rows, `APP_KPI_Report_${selQ}_FY${fy}.csv`);
  };
  const exportRiskReport = () => {
    const headers = ['Reference','Risk','Tier','Category','Inherent L','Inherent I','Inherent Score','Residual L','Residual I','Residual Score','Velocity','Owner','Appetite','Above Appetite','Controls','Treatments'];
    const rows = activeRisks.map(r => [r.reference, r.title, r.tier, r.category, r.inherentLikelihood, r.inherentImpact, r.inherentLikelihood*r.inherentImpact, r.residualLikelihood, r.residualImpact, r.residualLikelihood*r.residualImpact, r.velocity, r.riskOwner, r.appetiteThreshold, r.isAboveAppetite?'YES':'NO', (r.controls||[]).length, (r.treatmentPlans||[]).length]);
    exportCSV(headers, rows, `Enterprise_Risk_Report_FY${fy}.csv`);
  };
  const exportProjectReport = () => {
    const headers = ['Reference','Title','Type','Division','Status','Priority','TRL','Budget','Spent','Progress%','PI','PM','Funding Source','Research Theme'];
    const rows = projects.map(p => [p.reference, p.title, p.projectType, p.division?.name, p.status, p.priority, p.trlStage, p.totalBudget, p.actualSpend, p.progressPercentage, p.principalInvestigator, p.projectManager, p.fundingSource, p.researchTheme]);
    exportCSV(headers, rows, `Portfolio_Report_FY${fy}.csv`);
  };

  // ── Mintek PDF exports ──
  const pdfAppReport = () => {
    const html = `
      ${pdfSection('APP KPI Performance Report — ' + selQ + ' FY' + fy + '/' + ((fy+1)%100))}
      ${pdfTable(['Ref','KPI','Annual Target', selQ+' Target', selQ+' Actual', 'Variance %', 'Status'],
        appKpis.map(k => { const a=getQActual(k,selQ); const t=getQTarget(k,selQ); return [
          k.reference, k.name, k.annualTarget||'-', t||'-', a?.actualValue||'-',
          a?.variancePercentage!=null?a.variancePercentage.toFixed(1)+'%':'No data',
          a?.variancePercentage>=0?'On Track':a?.variancePercentage>=-15?'At Risk':'Behind'
        ];})
      )}
      ${pdfSection('Summary')}
      <div style="display:flex;gap:12px">${pdfStatCard('Total KPIs',appKpis.length)} ${pdfStatCard('On Track',appKpis.filter(k=>{const a=getQActual(k,selQ);return a?.variancePercentage>=0;}).length)} ${pdfStatCard('Behind',appKpis.filter(k=>{const a=getQActual(k,selQ);return a?.variancePercentage<-15;}).length)}</div>
    `;
    exportPdf('APP Performance Report', selQ + ' FY' + (fy-1) + '/' + (fy%100), html);
  };
  const pdfRiskReport = () => {
    const sorted = [...activeRisks].sort((a,b)=>(b.residualLikelihood*b.residualImpact)-(a.residualLikelihood*a.residualImpact));
    const html = `
      ${pdfSection('Enterprise Risk Register')}
      <div style="display:flex;gap:12px;margin-bottom:16px">${pdfStatCard('Total Risks',activeRisks.length)} ${pdfStatCard('High/Very High',activeRisks.filter(r=>r.residualLikelihood*r.residualImpact>12).length)} ${pdfStatCard('Above Appetite',activeRisks.filter(r=>r.appetiteThreshold&&r.residualLikelihood*r.residualImpact>r.appetiteThreshold).length)}</div>
      ${pdfTable(['Ref','Risk','Tier','Category','Inherent','Residual','Velocity','Owner','Controls'],
        sorted.map(r => [r.reference, r.title, r.tier, r.category, r.inherentLikelihood*r.inherentImpact, r.residualLikelihood*r.residualImpact, r.velocity||'-', r.riskOwner||'-', (r.controls||[]).length])
      )}
    `;
    exportPdf('Enterprise Risk Report', 'FY ' + (fy-1) + '/' + (fy%100), html);
  };
  const pdfPortfolioReport = () => {
    const html = `
      ${pdfSection('Project Portfolio Report')}
      <div style="display:flex;gap:12px;margin-bottom:16px">${pdfStatCard('Total Projects',projects.length)} ${pdfStatCard('Active',activeProjects.length)} ${pdfStatCard('Total Budget',fmtR(projects.reduce((s,p)=>s+(p.totalBudget||0),0)))} ${pdfStatCard('Spent',fmtR(projects.reduce((s,p)=>s+(p.actualSpend||0),0)))}</div>
      ${pdfTable(['Ref','Project','Type','Division','Status','Budget','Progress'],
        projects.map(p => [p.reference, p.title, p.projectType, p.division?.name||'-', p.status, p.totalBudget?'R'+(p.totalBudget/1000000).toFixed(1)+'M':'-', (p.progressPercentage||0)+'%'])
      )}
    `;
    exportPdf('Project Portfolio Report', 'FY ' + (fy-1) + '/' + (fy%100), html);
  };

  // ── Word equivalents — share the same HTML body the PDF exports use ──
  // The pdfTable/pdfSection/pdfStatCard helpers emit class-driven HTML and the
  // MintekWordExport stylesheet declares the same class names, so we can reuse
  // the body verbatim instead of building a parallel set of helpers.
  const wordAppReport = () => {
    const html = `
      ${pdfSection('APP KPI Performance Report — ' + selQ + ' FY' + fy + '/' + ((fy+1)%100))}
      ${pdfTable(['Ref','KPI','Annual Target', selQ+' Target', selQ+' Actual', 'Variance %', 'Status'],
        appKpis.map(k => { const a=getQActual(k,selQ); const t=getQTarget(k,selQ); return [
          k.reference, k.name, k.annualTarget||'-', t||'-', a?.actualValue||'-',
          a?.variancePercentage!=null?a.variancePercentage.toFixed(1)+'%':'No data',
          a?.variancePercentage>=0?'On Track':a?.variancePercentage>=-15?'At Risk':'Behind'
        ];})
      )}
      ${pdfSection('Summary')}
      <div>${pdfStatCard('Total KPIs',appKpis.length)} ${pdfStatCard('On Track',appKpis.filter(k=>{const a=getQActual(k,selQ);return a?.variancePercentage>=0;}).length)} ${pdfStatCard('Behind',appKpis.filter(k=>{const a=getQActual(k,selQ);return a?.variancePercentage<-15;}).length)}</div>
    `;
    exportWord('APP Performance Report', selQ + ' FY' + (fy-1) + '/' + (fy%100), html);
  };
  const wordRiskReport = () => {
    const sorted = [...activeRisks].sort((a,b)=>(b.residualLikelihood*b.residualImpact)-(a.residualLikelihood*a.residualImpact));
    const html = `
      ${pdfSection('Enterprise Risk Register')}
      <div>${pdfStatCard('Total Risks',activeRisks.length)} ${pdfStatCard('High/Very High',activeRisks.filter(r=>r.residualLikelihood*r.residualImpact>12).length)} ${pdfStatCard('Above Appetite',activeRisks.filter(r=>r.appetiteThreshold&&r.residualLikelihood*r.residualImpact>r.appetiteThreshold).length)}</div>
      ${pdfTable(['Ref','Risk','Tier','Category','Inherent','Residual','Velocity','Owner','Controls'],
        sorted.map(r => [r.reference, r.title, r.tier, r.category, r.inherentLikelihood*r.inherentImpact, r.residualLikelihood*r.residualImpact, r.velocity||'-', r.riskOwner||'-', (r.controls||[]).length])
      )}
    `;
    exportWord('Enterprise Risk Report', 'FY ' + (fy-1) + '/' + (fy%100), html);
  };
  const wordPortfolioReport = () => {
    const html = `
      ${pdfSection('Project Portfolio Report')}
      <div>${pdfStatCard('Total Projects',projects.length)} ${pdfStatCard('Active',activeProjects.length)} ${pdfStatCard('Total Budget',fmtR(projects.reduce((s,p)=>s+(p.totalBudget||0),0)))} ${pdfStatCard('Spent',fmtR(projects.reduce((s,p)=>s+(p.actualSpend||0),0)))}</div>
      ${pdfTable(['Ref','Project','Type','Division','Status','Budget','Progress'],
        projects.map(p => [p.reference, p.title, p.projectType, p.division?.name||'-', p.status, p.totalBudget?'R'+(p.totalBudget/1000000).toFixed(1)+'M':'-', (p.progressPercentage||0)+'%'])
      )}
    `;
    exportWord('Project Portfolio Report', 'FY ' + (fy-1) + '/' + (fy%100), html);
  };

  // ════════════════════════════════════════════════════════════════════════
  // FORECASTING COMPUTATIONS — memoised so they don't re-run on every render
  // ════════════════════════════════════════════════════════════════════════
  const kpiForecasts = useMemo(
    () => appKpis.map(k => projectKpiAnnual(k, fy)),
    [appKpis, fy]
  );
  const budgetForecasts = useMemo(
    () => activeProjects.map(p => projectBudget(p)),
    [activeProjects]
  );
  const completionForecasts = useMemo(
    () => activeProjects.map(p => projectCompletion(p)),
    [activeProjects]
  );
  const riskTrends = useMemo(
    () => activeRisks.map(r => riskTrendDirection(r)),
    [activeRisks]
  );

  const pdfForecastReport = () => {
    const html = `
      ${pdfSection('Forecasting & Projections — FY' + fy + '/' + ((fy+1)%100))}
      <p style="font-size:9pt;color:#64748b;margin-bottom:8pt">Forecasts use ordinary-least-squares regression on actuals to date. Confidence reflects the number of data points and goodness-of-fit (R²).</p>
      ${pdfSection('KPI Year-End Projection')}
      ${pdfTable(['Ref','KPI','Annual Target','Projected','Variance %','Confidence','Status'],
        kpiForecasts.map(f => [
          f.reference, f.name, f.annualTarget||'—',
          f.projectedAnnual!=null ? Math.round(f.projectedAnnual*100)/100 : '—',
          f.variance!=null ? f.variance.toFixed(1)+'%' : '—',
          f.confidence.label, f.status
        ])
      )}
      ${pdfSection('Budget Burn-Rate Forecast')}
      ${pdfTable(['Ref','Project','Budget','Spent','Monthly Burn','Forecast Final','Variance %','Status'],
        budgetForecasts.map(f => [
          f.reference, f.title, fmtR(f.budget), fmtR(f.spent),
          f.monthlyBurn!=null ? fmtR(f.monthlyBurn) : '—',
          f.forecastFinalSpend!=null ? fmtR(f.forecastFinalSpend) : '—',
          f.variance!=null ? f.variance.toFixed(1)+'%' : '—',
          f.status
        ])
      )}
      ${pdfSection('Project Completion Forecast')}
      ${pdfTable(['Ref','Project','Progress','Velocity (%/mo)','Planned End','Forecast End','Slip (days)','Status'],
        completionForecasts.map(f => [
          f.reference, f.title, f.progress+'%',
          f.velocity!=null ? f.velocity : '—',
          f.plannedEnd ? new Date(f.plannedEnd).toLocaleDateString('en-ZA') : '—',
          f.forecastEnd ? new Date(f.forecastEnd).toLocaleDateString('en-ZA') : '—',
          f.slipDays!=null ? f.slipDays : '—',
          f.status
        ])
      )}
    `;
    exportPdf('Forecasting & Projections', 'FY ' + (fy-1) + '/' + (fy%100), html);
  };
  const wordForecastReport = () => {
    const html = `
      ${pdfSection('Forecasting & Projections — FY' + fy + '/' + ((fy+1)%100))}
      <p>Forecasts use ordinary-least-squares regression on actuals to date. Confidence reflects the number of data points and goodness-of-fit (R²).</p>
      ${pdfSection('KPI Year-End Projection')}
      ${pdfTable(['Ref','KPI','Annual Target','Projected','Variance %','Confidence','Status'],
        kpiForecasts.map(f => [f.reference, f.name, f.annualTarget||'—',
          f.projectedAnnual!=null ? Math.round(f.projectedAnnual*100)/100 : '—',
          f.variance!=null ? f.variance.toFixed(1)+'%' : '—',
          f.confidence.label, f.status]))}
      ${pdfSection('Budget Burn-Rate Forecast')}
      ${pdfTable(['Ref','Project','Budget','Spent','Forecast Final','Variance %','Status'],
        budgetForecasts.map(f => [f.reference, f.title, fmtR(f.budget), fmtR(f.spent),
          f.forecastFinalSpend!=null ? fmtR(f.forecastFinalSpend) : '—',
          f.variance!=null ? f.variance.toFixed(1)+'%' : '—', f.status]))}
      ${pdfSection('Project Completion Forecast')}
      ${pdfTable(['Ref','Project','Progress','Planned End','Forecast End','Slip','Status'],
        completionForecasts.map(f => [f.reference, f.title, f.progress+'%',
          f.plannedEnd ? new Date(f.plannedEnd).toLocaleDateString('en-ZA') : '—',
          f.forecastEnd ? new Date(f.forecastEnd).toLocaleDateString('en-ZA') : '—',
          f.slipDays!=null ? f.slipDays+'d' : '—', f.status]))}
    `;
    exportWord('Forecasting & Projections', 'FY ' + (fy-1) + '/' + (fy%100), html);
  };

  // ════════════════════════════════════════════════════════════════════════
  // PORTFOLIO BALANCING — analytics engine
  // ════════════════════════════════════════════════════════════════════════
  // Computes the balancing-recommendation list shown in the Portfolio Health
  // view. Each rule fires independently and returns an object that the UI can
  // render. Severity drives the colour band on the recommendation card.
  const balancingRecommendations = useMemo(() => {
    const recs = [];
    const total = projects.length || 1;
    const totalBudget = projects.reduce((s,p)=>s+(p.totalBudget||0),0) || 1;

    // 1. TRL pipeline gap — too much weight at low TRL stages signals a
    // commercialisation gap further along the pipeline.
    const lowTrlBudget = projects.filter(p => (p.trlStage||1) <= 3).reduce((s,p)=>s+(p.totalBudget||0),0);
    const lowTrlPct = (lowTrlBudget / totalBudget) * 100;
    if (lowTrlPct >= 60) {
      recs.push({
        severity: 'high', title: 'Pipeline maturity gap',
        body: `${lowTrlPct.toFixed(0)}% of portfolio budget sits at TRL 1–3. Consider stage-gating early-stage projects and routing more spend toward TRL 6+ to balance the commercialisation pipeline.`
      });
    } else if (lowTrlPct <= 15) {
      recs.push({
        severity: 'low', title: 'Innovation pipeline thinning',
        body: `Only ${lowTrlPct.toFixed(0)}% of portfolio budget is at TRL 1–3. This protects near-term delivery but may starve the future pipeline. Consider funding new exploratory projects.`
      });
    }

    // 2. Project type ratio — recommended Research:Commercial:Contract ≈ 50:30:20
    const counts = {
      Research: projects.filter(p=>p.projectType==='Research').length,
      Commercial: projects.filter(p=>p.projectType==='Commercial').length,
      Contract: projects.filter(p=>p.projectType==='Contract').length
    };
    const rPct = (counts.Research/total)*100;
    const cPct = (counts.Commercial/total)*100;
    if (rPct >= 75) {
      recs.push({
        severity: 'medium', title: 'Project-type concentration',
        body: `${rPct.toFixed(0)}% of projects are Research-only. Recommended balance is ~50% Research / 30% Commercial / 20% Contract. Consider commercial-revenue diversification.`
      });
    }
    if (cPct < 10 && projects.length >= 5) {
      recs.push({
        severity: 'medium', title: 'Low commercial mix',
        body: `Only ${cPct.toFixed(0)}% of projects are Commercial. Increasing this protects state-grant dependency.`
      });
    }

    // 3. Budget concentration — top 5 projects should not exceed 80% of budget
    const top5Budget = [...projects].sort((a,b)=>(b.totalBudget||0)-(a.totalBudget||0)).slice(0,5).reduce((s,p)=>s+(p.totalBudget||0),0);
    const top5Pct = (top5Budget / totalBudget) * 100;
    if (top5Pct >= 80 && projects.length >= 8) {
      recs.push({
        severity: 'high', title: 'Budget concentration risk',
        body: `Top 5 projects consume ${top5Pct.toFixed(0)}% of the portfolio budget. Failure or delay in any one would have outsized impact. Consider splitting major programmes into tranches or accelerating smaller initiatives.`
      });
    }

    // 4. Funding source diversification
    const sources = {};
    projects.forEach(p => { const s = p.fundingSource || 'Unspecified'; sources[s] = (sources[s]||0) + (p.totalBudget||0); });
    const sourceEntries = Object.entries(sources).filter(([k]) => k !== 'Unspecified').sort((a,b)=>b[1]-a[1]);
    if (sourceEntries.length > 0) {
      const topSourcePct = (sourceEntries[0][1] / totalBudget) * 100;
      if (topSourcePct >= 70) {
        recs.push({
          severity: 'medium', title: 'Funding-source concentration',
          body: `${topSourcePct.toFixed(0)}% of portfolio budget comes from a single source (${sourceEntries[0][0]}). Diversifying funding sources reduces external risk.`
        });
      }
    }

    // 5. Division load balance — flag divisions running >150% of fair share
    const fairShare = total / Math.max(1, divisions.length);
    divisions.forEach(div => {
      const divCount = projects.filter(p => p.division?.id === div.id || p.divisionId === div.id).length;
      if (divCount >= fairShare * 1.6 && divCount >= 5) {
        recs.push({
          severity: 'low', title: `${div.name} overloaded`,
          body: `${divCount} active projects against an average of ${fairShare.toFixed(1)} per division. Consider rebalancing or adding capacity.`
        });
      }
    });

    return recs;
  }, [projects, divisions]);

  // ════════════════════════════════════════════════════════════════════════
  // CUSTOM REPORT BUILDER — config schema + runner
  // ════════════════════════════════════════════════════════════════════════
  // Field catalogue by data source — used by the builder modal to populate
  // the field-picker. Keep keys snake_case-ish so they round-trip cleanly
  // through the ConfigurationJson column.
  const FIELD_CATALOGUE = {
    KPIs: [
      {key:'reference', label:'Reference'},
      {key:'name', label:'Name'},
      {key:'division', label:'Division'},
      {key:'isAppIndicator', label:'APP Indicator'},
      {key:'annualTarget', label:'Annual Target'},
      {key:'q1Target', label:'Q1 Target'}, {key:'q1Actual', label:'Q1 Actual'},
      {key:'q2Target', label:'Q2 Target'}, {key:'q2Actual', label:'Q2 Actual'},
      {key:'q3Target', label:'Q3 Target'}, {key:'q3Actual', label:'Q3 Actual'},
      {key:'q4Target', label:'Q4 Target'}, {key:'q4Actual', label:'Q4 Actual'},
      {key:'variance', label:'Variance %'},
      {key:'owner', label:'KPI Owner'}
    ],
    Risks: [
      {key:'reference', label:'Reference'},
      {key:'title', label:'Title'},
      {key:'tier', label:'Tier'},
      {key:'category', label:'Category'},
      {key:'inherentScore', label:'Inherent Score'},
      {key:'residualScore', label:'Residual Score'},
      {key:'velocity', label:'Velocity'},
      {key:'appetiteThreshold', label:'Appetite Threshold'},
      {key:'isAboveAppetite', label:'Above Appetite'},
      {key:'riskOwner', label:'Risk Owner'},
      {key:'controlCount', label:'Control Count'}
    ],
    Projects: [
      {key:'reference', label:'Reference'},
      {key:'title', label:'Title'},
      {key:'projectType', label:'Project Type'},
      {key:'division', label:'Division'},
      {key:'status', label:'Status'},
      {key:'priority', label:'Priority'},
      {key:'trlStage', label:'TRL Stage'},
      {key:'totalBudget', label:'Total Budget'},
      {key:'actualSpend', label:'Actual Spend'},
      {key:'progressPercentage', label:'Progress %'},
      {key:'principalInvestigator', label:'Principal Investigator'},
      {key:'projectManager', label:'Project Manager'},
      {key:'fundingSource', label:'Funding Source'},
      {key:'researchTheme', label:'Research Theme'}
    ],
    Divisions: [
      {key:'name', label:'Name'},
      {key:'cluster', label:'Cluster'},
      {key:'manager', label:'Manager'}
    ],
    Objectives: [
      {key:'reference', label:'Reference'},
      {key:'title', label:'Title'},
      {key:'progressPercentage', label:'Progress %'},
      {key:'financialYear', label:'Financial Year'}
    ],
    // V3.4 — Grants as a data source for the Custom Report Builder
    Grants: [
      {key:'referenceNo', label:'Reference'},
      {key:'funderReference', label:'Funder Reference'},
      {key:'title', label:'Title'},
      {key:'funder', label:'Funder'},
      {key:'programme', label:'Programme'},
      {key:'status', label:'Status'},
      {key:'totalAwarded', label:'Total Awarded'},
      {key:'totalReceived', label:'Total Received'},
      {key:'totalSpent', label:'Total Spent'},
      {key:'remaining', label:'Remaining'},
      {key:'currency', label:'Currency'},
      {key:'applicationDate', label:'Application Date'},
      {key:'awardDate', label:'Award Date'},
      {key:'startDate', label:'Start Date'},
      {key:'endDate', label:'End Date'},
      {key:'grantManagerName', label:'Grant Manager'},
      {key:'grantManagerEmail', label:'Manager Email'},
      {key:'projectCount', label:'Linked Projects'}
    ]
  };

  // Map a row of the source dataset to the field values requested in the config.
  // Returns an array of { headers, rows } ready for any of the export formats.
  const materialiseCustomReport = useCallback((config) => {
    const ds = config.dataSource;
    const fields = config.fields || [];
    const filters = config.filters || {};
    const sortBy = config.sortBy;
    const sortOrder = config.sortOrder || 'asc';
    const catalogue = FIELD_CATALOGUE[ds] || [];
    const labels = fields.map(f => (catalogue.find(c=>c.key===f) || {label:f}).label);

    let source = [];
    if (ds === 'KPIs') {
      source = kpis.filter(k => {
        if (filters.isAppIndicator != null && !!k.isAppIndicator !== !!filters.isAppIndicator) return false;
        if (filters.divisionId && k.divisionId !== filters.divisionId) return false;
        if (filters.financialYear && k.financialYear !== filters.financialYear) return false;
        return true;
      });
    } else if (ds === 'Risks') {
      source = risks.filter(r => {
        if (filters.tier && r.tier !== filters.tier) return false;
        if (filters.category && r.category !== filters.category) return false;
        if (filters.isAboveAppetite && !r.isAboveAppetite) return false;
        if (filters.status && r.status !== filters.status) return false;
        return true;
      });
    } else if (ds === 'Projects') {
      source = projects.filter(p => {
        if (filters.divisionId && p.divisionId !== filters.divisionId) return false;
        if (filters.projectType && p.projectType !== filters.projectType) return false;
        if (filters.status) {
          const s = Array.isArray(filters.status) ? filters.status : [filters.status];
          if (!s.includes(p.status)) return false;
        }
        if (filters.priority && p.priority !== filters.priority) return false;
        return true;
      });
    } else if (ds === 'Divisions') {
      source = divisions;
    } else if (ds === 'Objectives') {
      source = objectives;
    } else if (ds === 'Grants') {
      // V3.4 — Grants data source
      source = grants.filter(g => {
        if (filters.status && g.status !== filters.status) return false;
        if (filters.funder && g.funder !== filters.funder) return false;
        if (filters.programme && g.programme !== filters.programme) return false;
        return true;
      });
    }

    // Sort
    if (sortBy) {
      source = [...source].sort((a, b) => {
        const av = valueOf(a, sortBy, ds);
        const bv = valueOf(b, sortBy, ds);
        if (av == null) return 1;
        if (bv == null) return -1;
        const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return sortOrder === 'desc' ? -cmp : cmp;
      });
    }

    const rows = source.map(item => fields.map(f => valueOf(item, f, ds)));

    return { headers: labels, rows, count: source.length };
  }, [kpis, risks, projects, divisions, objectives, grants]);

  // Per-data-source field accessor — handles the few fields that need computing.
  function valueOf(item, key, ds) {
    if (ds === 'KPIs') {
      switch (key) {
        case 'division': return item.division?.name || '';
        case 'isAppIndicator': return item.isAppIndicator ? 'Yes' : 'No';
        case 'q1Actual': return (item.actuals||[]).find(a=>a.quarter==='Q1')?.actualValue ?? '';
        case 'q2Actual': return (item.actuals||[]).find(a=>a.quarter==='Q2')?.actualValue ?? '';
        case 'q3Actual': return (item.actuals||[]).find(a=>a.quarter==='Q3')?.actualValue ?? '';
        case 'q4Actual': return (item.actuals||[]).find(a=>a.quarter==='Q4')?.actualValue ?? '';
        case 'variance': {
          const sumActual = (item.actuals||[]).reduce((s,a)=>s+(a.actualValue||0),0);
          const target = item.annualTarget || 0;
          return target ? (((sumActual-target)/target)*100).toFixed(1)+'%' : '';
        }
        default: return item[key] ?? '';
      }
    }
    if (ds === 'Risks') {
      switch (key) {
        case 'inherentScore': return (item.inherentLikelihood||0)*(item.inherentImpact||0);
        case 'residualScore': return (item.residualLikelihood||0)*(item.residualImpact||0);
        case 'isAboveAppetite': return item.isAboveAppetite ? 'Yes' : 'No';
        case 'controlCount': return (item.controls||[]).length;
        default: return item[key] ?? '';
      }
    }
    if (ds === 'Projects') {
      switch (key) {
        case 'division': return item.division?.name || '';
        case 'totalBudget': return item.totalBudget ?? '';
        case 'actualSpend': return item.actualSpend ?? '';
        case 'progressPercentage': return (item.progressPercentage||0)+'%';
        default: return item[key] ?? '';
      }
    }
    if (ds === 'Divisions') {
      switch (key) {
        case 'cluster': return item.cluster?.name || '';
        default: return item[key] ?? '';
      }
    }
    if (ds === 'Grants') {
      // V3.4 — Grants field accessor
      const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-ZA') : '';
      switch (key) {
        case 'totalAwarded':    return item.totalAwarded ?? '';
        case 'totalReceived':   return item.totalReceived ?? '';
        case 'totalSpent':      return item.totalSpent ?? '';
        case 'remaining':       return (item.totalAwarded ?? 0) - (item.totalSpent ?? 0);
        case 'applicationDate': return fmtDate(item.applicationDate);
        case 'awardDate':       return fmtDate(item.awardDate);
        case 'startDate':       return fmtDate(item.startDate);
        case 'endDate':         return fmtDate(item.endDate);
        case 'projectCount':    return item.projectCount ?? 0;
        default:                return item[key] ?? '';
      }
    }
    return item[key] ?? '';
  }

  // Execute a saved or freshly-built template
  const runCustomReport = async (template) => {
    let config;
    try { config = typeof template.configurationJson === 'string' ? JSON.parse(template.configurationJson) : template.configurationJson; }
    catch { alert('Template configuration is invalid'); return; }

    const { headers, rows, count } = materialiseCustomReport({ dataSource: template.dataSource, ...config });

    if (count === 0) { alert('The current filters produced 0 rows.'); return; }

    const filename = (template.name || 'Custom_Report').replace(/[^a-z0-9]+/gi,'_');
    const subtitle = `Data source: ${template.dataSource} · ${count} rows · FY ${fy}/${(fy+1)%100}`;

    if (template.outputFormat === 'Excel') {
      exportExcel(template.name, headers, rows, filename);
    } else {
      const summary = config.includeExecSummary
        ? `${pdfSection('Executive Summary')}<p>This report was generated from the <b>${template.dataSource}</b> data source on ${new Date().toLocaleDateString('en-ZA')}. ${count} record(s) matched the configured filters.</p>`
        : '';
      const cover = config.includeCoverPage
        ? `<div style="page-break-after:always">${pdfSection(template.name)}<p>${template.description||''}</p></div>`
        : '';
      const html = `${cover}${summary}${pdfSection(template.name)}${pdfTable(headers, rows)}`;
      if (template.outputFormat === 'Word') exportWord(template.name, subtitle, html);
      else exportPdf(template.name, subtitle, html);
    }

    // Best-effort run-count tracking; ignore failures (e.g. ad-hoc unsaved templates)
    if (template.id) {
      try { await reportTemplatesApi.recordRun(template.id); } catch {}
    }
  };

  const saveTemplate = async (template) => {
    try {
      const payload = {
        name: template.name,
        description: template.description,
        dataSource: template.dataSource,
        outputFormat: template.outputFormat,
        configurationJson: typeof template.configurationJson === 'string'
          ? template.configurationJson
          : JSON.stringify(template.configurationJson || {}),
        isShared: !!template.isShared
      };
      if (template.id) {
        await reportTemplatesApi.update(template.id, payload);
      } else {
        await reportTemplatesApi.create(payload);
      }
      setBuilderOpen(false);
      setEditingTemplate(null);
      const res = await reportTemplatesApi.getAll();
      setTemplates(res.data || []);
    } catch (e) {
      alert('Could not save template: ' + (e.response?.data?.title || e.message));
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this report template?')) return;
    try {
      await reportTemplatesApi.delete(id);
      setTemplates(templates.filter(t => t.id !== id));
    } catch (e) {
      alert('Could not delete: ' + (e.response?.data?.title || e.message));
    }
  };

  const VIEWS = [
    {v:'executive',l:'Executive Summary',I:PieChart},
    {v:'performance',l:'APP Performance',I:BarChart3},
    {v:'risk',l:'Enterprise Risk',I:ShieldCheck},
    {v:'portfolio',l:'Portfolio Health',I:FolderKanban},
    {v:'forecasting',l:'Forecasting',I:TrendingUp},
    {v:'reports',l:'Report Generator',I:FileSpreadsheet},
    {v:'workflows',l:'Approvals',I:CheckCircle2}
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-400 text-sm">Loading reporting data...</div></div>;

  // Precomputed totals for the BUDGET UTILISATION donut
  const totalBudget = projects.reduce((s,p)=>s+(p.totalBudget||0),0);
  const totalSpent  = projects.reduce((s,p)=>s+(p.actualSpend||0),0);
  const utilisation = totalBudget > 0 ? (totalSpent/totalBudget)*100 : 0;

  return (
    <div className={view==='executive' ? 'min-h-screen bg-[#eef2f3] -m-6' : 'space-y-6'}>
      {/* ─── Page header (matches all 6 screenshots) ─── */}
      <div className={view==='executive' ? 'bg-[#263744] px-5 pt-4 pb-2' : 'flex items-end justify-between flex-wrap gap-3'}>
        <div className={view==='executive' ? 'flex items-start justify-between flex-wrap gap-3 w-full' : 'flex items-end justify-between flex-wrap gap-3 w-full'}>
          <div>
            <h1 className={view==='executive' ? 'font-bold text-xl text-white leading-tight' : 'font-bold text-2xl text-slate-800'}>Reporting &amp; Analytics</h1>
            <p className={view==='executive' ? 'text-slate-200 text-[11px] mt-0.5' : 'text-slate-500 text-sm mt-1'}>FY {fy}/{(fy+1)%100} · Real-time evidence-based decision support</p>
          </div>
          <div className="flex items-center gap-2">
            <select className={view==='executive' ? 'px-2 py-1 rounded bg-white text-slate-700 text-[10px] border-0 focus:outline-none' : input+' max-w-[110px]'} value={fy} onChange={e=>setFy(+e.target.value)}>
              <option value={2026}>FY 25/26</option>
              <option value={2027}>FY 26/27</option>
            </select>
            <select className={view==='executive' ? 'px-2 py-1 rounded bg-white text-slate-700 text-[10px] border-0 focus:outline-none' : input+' max-w-[80px]'} value={selQ} onChange={e=>setSelQ(e.target.value)}>
              {QS.map(q=><option key={q}>{q}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ─── Tab bar — underline indicator (matches screenshots) ─── */}
      <div className={view==='executive' ? 'bg-[#263744] px-5 pb-2' : 'border-b border-slate-200'}>
        <div className={view==='executive' ? 'flex overflow-x-auto gap-1' : 'flex overflow-x-auto gap-1'}>
          {VIEWS.map(t=>{
            const Icon = t.I;
            const active = view === t.v;
            return (
              <button key={t.v} onClick={()=>setView(t.v)}
                className={view==='executive'
                  ? `px-3 py-1.5 rounded-sm text-[10px] font-semibold whitespace-nowrap flex items-center gap-1.5 transition-colors ${active ? 'bg-white text-slate-800' : 'text-slate-200 hover:bg-white/10'}`
                  : `px-4 py-2.5 text-sm font-semibold whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${active ? 'border-[#c8102e] text-[#c8102e]' : 'border-transparent text-slate-500 hover:text-slate-700'}`
                }>
                <Icon size={view==='executive'?11:14}/>{t.l}
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══════════ EXECUTIVE SUMMARY (Image 4) ═══════════ */}
      {view==='executive'&&(<div className="px-5 py-4 space-y-4">
        {/* Top-level KPIs — 5 stat cards with left-border accent */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5">
          {[
            {l:'Strategic Objectives', v:objectives.length, c:'border-[#8b1e2d]', I:Target},
            {l:'Active Projects', v:activeProjects.length, c:'border-[#8b1e2d]', I:BarChart3},
            {l:'APP KPIs On Track', v:appKpis.filter(k=>{const a=getQActual(k,selQ);return a&&a.variancePercentage>=0;}).length+'/'+appKpis.length, c:'border-[#8b1e2d]', I:BarChart3},
            {l:'High/Very High Risks', v:activeRisks.filter(r=>r.residualLikelihood*r.residualImpact>12).length, c:'border-[#8b1e2d]', I:ShieldCheck, badge:'High Risk', badgeCls:'bg-[#8b1e2d] text-white'},
            {l:'Portfolio Budget', v:fmtR(totalBudget), c:'border-[#8b1e2d]', I:FolderKanban}
          ].map((c,i)=>{
            const Icon = c.I;
            return (
              <div key={i} className={`bg-white rounded-md border ${c.c} shadow-sm px-3 py-2.5 min-h-[62px]`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[8px] font-bold text-[#8b1e2d] uppercase tracking-wide">{c.l}</p>
                    <div className="mt-1 flex items-end gap-2">
                      <p className="text-[18px] leading-none font-black text-[#1f2933]">{c.v}</p>
                      {c.badge && <span className={`inline-block px-2 py-0.5 rounded text-[8px] leading-none font-bold ${c.badgeCls}`}>{c.badge}</span>}
                    </div>
                  </div>
                  <Icon size={18} className="text-[#8b1e2d] shrink-0 opacity-80"/>
                </div>
              </div>
            );
          })}
        </div>

        {/* Strategy → Initiative → Project → Risk Linkage */}
        <div className="bg-white rounded-md shadow-sm border border-slate-200 px-4 py-3">
          <h3 className="text-[10px] font-bold text-slate-800 uppercase tracking-wide mb-3">Strategy → Initiative → Project → Risk Linkage</h3>
          <div className="space-y-2">
            {objectives.slice(0,6).map(so => {
              const soKpis = kpis.filter(k=>k.strategicObjectiveId===so.id);
              const soRisks = risks.filter(r=>r.strategicObjectiveId===so.id);
              const soInits = so.initiatives||[];
              const soProjectsFromFK = soInits.flatMap(i=>i.projects||[]);
              const soProjectsFromJunction = (projects||[]).filter(p=>{try{return(p.strategicObjectives||[]).some(x=>x.strategicObjectiveId===so.id);}catch{return false;}});
              const seen = new Set(soProjectsFromFK.map(p=>p.id));
              const soProjects = [...soProjectsFromFK, ...soProjectsFromJunction.filter(p=>!seen.has(p.id))];
              const soProgress = so.progressPercentage||0;
              return (
                <div key={so.id} className="bg-[#f7f9f9] border border-slate-100 rounded-md px-3 py-2">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-mono text-[8px] font-bold text-[#8b1e2d] bg-red-50 border border-red-100 px-1.5 py-0.5 rounded">{so.reference}</span>
                    <span className="text-[10px] font-semibold text-slate-800 flex-1 truncate">{so.title}</span>
                    <span className="text-[10px] font-black text-[#8b1e2d]">{soProgress}%</span>
                  </div>
                  <div className="h-1 rounded-full bg-slate-100 mb-1.5 overflow-hidden">
                    <div className="h-full rounded-full transition-all bg-[#8b1e2d]" style={{width:`${soProgress}%`}}/>
                  </div>
                  <div className="flex items-center gap-1.5 text-[8px] text-slate-500 flex-wrap">
                    <span className="bg-white border border-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{soKpis.length} KPIs</span>
                    <span className="bg-white border border-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{soInits.length} Programmes</span>
                    <span className="bg-white border border-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{soProjects.length} Projects</span>
                    <span className="bg-red-50 text-[#8b1e2d] px-2 py-0.5 rounded-full">{soRisks.length} Risks</span>
                    {soRisks.filter(r=>r.residualLikelihood*r.residualImpact>12).length>0&&
                      <span className="bg-[#8b1e2d] text-white px-2 py-0.5 rounded-full font-bold">{soRisks.filter(r=>r.residualLikelihood*r.residualImpact>12).length} High Risk</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Combined KPI + Risk snapshot */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="bg-[#263744] rounded-md shadow-sm border border-[#263744] px-4 py-3 text-slate-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[10px] font-bold text-white uppercase tracking-wide">APP KPI Performance — {selQ}</h3>
              <button onClick={exportKpiReport} className="px-2 py-1 rounded bg-[#334957] text-white text-[8px] font-semibold hover:bg-[#3d5362] transition-colors flex items-center gap-1"><Download size={9}/> Export CSV</button>
            </div>
            {appKpis.slice(0,8).map(k=>{
              const a=getQActual(k,selQ);
              const t=getQTarget(k,selQ);
              return (
                <div key={k.id} className="flex items-center gap-2 mb-1.5 text-[9px]">
                  <span className="font-mono text-slate-300 w-14 flex-shrink-0">{k.reference}</span>
                  <span className="text-slate-100 flex-1 truncate">{k.name}</span>
                  <span className="font-mono w-8 text-right text-white">{a?.actualValue??'—'}</span>
                  <span className="text-slate-400 w-4 text-center">/</span>
                  <span className="font-mono w-8 text-right text-slate-300">{t??'—'}</span>
                  {a?.variancePercentage!=null
                    ? <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full w-12 text-center ${a.variancePercentage>=0?'bg-red-100 text-[#8b1e2d]':a.variancePercentage>=-15?'bg-red-50 text-[#8b1e2d]':'bg-[#8b1e2d] text-white'}`}>{a.variancePercentage>0?'+':''}{a.variancePercentage.toFixed(1)}%</span>
                    : <span className="w-12 text-center text-[8px] text-slate-400">—</span>}
                </div>
              );
            })}
          </div>
          <div className="bg-[#263744] rounded-md shadow-sm border border-[#263744] px-4 py-3 text-slate-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[10px] font-bold text-white uppercase tracking-wide">Top Risks by Residual Score</h3>
              <button onClick={exportRiskReport} className="px-2 py-1 rounded bg-[#334957] text-white text-[8px] font-semibold hover:bg-[#3d5362] transition-colors flex items-center gap-1"><Download size={9}/> Export CSV</button>
            </div>
            {activeRisks.sort((a,b)=>(b.residualLikelihood*b.residualImpact)-(a.residualLikelihood*a.residualImpact)).slice(0,8).map(r=>{
              const rs=r.residualLikelihood*r.residualImpact;
              return (
                <div key={r.id} className="flex items-center gap-2 mb-1.5 text-[9px]">
                  <span className="font-mono text-slate-300 w-14 flex-shrink-0">{r.reference}</span>
                  <span className="text-slate-100 flex-1 truncate">{r.title}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold w-8 text-center ${rs>12?'bg-[#8b1e2d] text-white':rs>8?'bg-red-100 text-[#8b1e2d]':'bg-white text-slate-700'}`}>{rs}</span>
                  {r.isAboveAppetite&&<span className="text-[8px] text-red-200 font-bold" title="Above appetite">↑</span>}
                  <span className="text-[8px] text-slate-300 w-16 truncate text-right">{r.riskOwner||'—'}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>)}

      {/* ═══════════ APP PERFORMANCE (Image 1) ═══════════ */}
      {view==='performance'&&(<div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-bold text-slate-700">APP Performance Report — {selQ} FY{fy}/{(fy+1)%100}</h3>
            <div className="flex gap-2">
              <button onClick={pdfAppReport} className={btnS}><Printer size={12}/> PDF</button>
              <button onClick={wordAppReport} className={btnS}><FileText size={12}/> Word</button>
              <button onClick={exportKpiReport} className={btnS}><FileSpreadsheet size={12}/> Excel</button>
            </div>
          </div>
        </div>

        {/* Multi-Quarter Trend Table */}
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="p-4 border-b border-slate-100"><h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Quarterly Trend — All APP KPIs</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-500 uppercase tracking-wider">Ref</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-500 uppercase tracking-wider">KPI</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-500 uppercase tracking-wider">Division</th>
                  <th className="px-3 py-2.5 text-center font-semibold text-slate-500 uppercase tracking-wider">Annual</th>
                  {QS.map(q=>(
                    <th key={q} className="px-3 py-2.5 text-center font-semibold text-slate-500 uppercase tracking-wider border-l border-slate-200" colSpan={2}>
                      {q}<br/><span className="text-[8px] font-normal normal-case">Tgt / Act</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {appKpis.map(k=>(
                  <tr key={k.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2.5 font-mono text-[10px] text-[#c8102e] font-bold">{k.reference}</td>
                    <td className="px-3 py-2.5 text-slate-700 max-w-[280px] truncate">{k.name}</td>
                    <td className="px-3 py-2.5 text-slate-400 text-[10px]">{k.division?.name||'—'}</td>
                    <td className="px-3 py-2.5 text-center font-bold">{k.annualTarget??'—'}</td>
                    {QS.map(q=>{
                      const a=getQActual(k,q);
                      const t=getQTarget(k,q);
                      return (
                        <React.Fragment key={q}>
                          <td className="px-1.5 py-2.5 text-center text-slate-400 border-l border-slate-100">{t??'—'}</td>
                          <td className="px-1.5 py-2.5 text-center">
                            {a ? <span className={`font-bold ${varColor(a.variancePercentage)}`}>{a.actualValue}</span> : <span className="text-slate-300">—</span>}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Divisional Performance Heatmap */}
        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Cross-Divisional Performance Heatmap — {selQ}</h3>
          <div className="space-y-2">
            {divisions.map(div=>{
              const divKpis=kpis.filter(k=>k.divisionId===div.id);
              if(!divKpis.length)return null;
              const onTrack=divKpis.filter(k=>{const a=getQActual(k,selQ);return a&&a.variancePercentage!=null&&a.variancePercentage>=0;}).length;
              const behind=divKpis.filter(k=>{const a=getQActual(k,selQ);return a&&a.variancePercentage!=null&&a.variancePercentage<0;}).length;
              const noData=divKpis.length-onTrack-behind;
              return (
                <div key={div.id} className="flex items-center gap-3">
                  <span className="text-xs text-slate-700 w-36 truncate">{div.name}</span>
                  <div className="flex-1 flex h-5 rounded-full overflow-hidden bg-slate-100">
                    {onTrack>0&&<div className="bg-emerald-400 flex items-center justify-center text-white text-[9px] font-bold" style={{width:`${(onTrack/divKpis.length)*100}%`}}>{onTrack}</div>}
                    {behind>0&&<div className="bg-red-400 flex items-center justify-center text-white text-[9px] font-bold" style={{width:`${(behind/divKpis.length)*100}%`}}>{behind}</div>}
                    {noData>0&&<div className="bg-slate-200 flex items-center justify-center text-slate-500 text-[9px] font-bold" style={{width:`${(noData/divKpis.length)*100}%`}}>{noData}</div>}
                  </div>
                  <span className="text-xs text-slate-500 w-16 text-right">{divKpis.length} KPIs</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3 text-[10px] text-slate-400">
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-400"/>On Track</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-400"/>Behind</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-slate-200"/>No Data</span>
          </div>
        </div>

        {/* Performance Gap Analysis */}
        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Performance Gap Analysis — {selQ}</h3>
          {appKpis.filter(k=>{const a=getQActual(k,selQ);return a&&a.variancePercentage!=null&&a.variancePercentage<0;}).sort((a,b)=>{const va=getQActual(a,selQ)?.variancePercentage||0;const vb=getQActual(b,selQ)?.variancePercentage||0;return va-vb;}).slice(0,5).map(k=>{
            const a=getQActual(k,selQ);
            return (
              <div key={k.id} className="bg-red-50 rounded-xl p-4 mb-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-slate-800">{k.reference} — {k.name}</span>
                  <span className={`font-bold text-sm ${varColor(a.variancePercentage)}`}>{a.variancePercentage.toFixed(1)}%</span>
                </div>
                {a.deviationReason&&<p className="text-[10px] text-red-700 mb-0.5"><strong>Reason:</strong> {a.deviationReason}</p>}
                {a.correctiveAction&&<p className="text-[10px] text-blue-700"><strong>Corrective Action:</strong> {a.correctiveAction}</p>}
              </div>
            );
          })}
          {appKpis.filter(k=>{const a=getQActual(k,selQ);return a&&a.variancePercentage!=null&&a.variancePercentage<0;}).length===0&&<p className="text-sm text-slate-400 text-center py-4">No performance gaps in {selQ}</p>}
        </div>
      </div>)}

      {/* ═══════════ ENTERPRISE RISK (Image 3) ═══════════ */}
      {view==='risk'&&(<div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-[28px] font-bold text-slate-800">Enterprise Risk Dashboard — Board Ready</h3>
          <div className="flex gap-2">
            <button onClick={pdfRiskReport} className={pdfBtn}><Printer size={14}/> PDF</button>
            <button onClick={wordRiskReport} className={btnS}><FileText size={14}/> Word</button>
            <button onClick={exportRiskReport} className={btnS}><FileSpreadsheet size={14}/> Excel</button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {[
            {l:'Total Active Risks',v:activeRisks.length,c:'border-slate-300',dot:'bg-slate-400'},
            {l:'High / Very High',v:activeRisks.filter(r=>r.residualLikelihood*r.residualImpact>12).length,c:'border-[#8f1024]',dot:'bg-[#8f1024]'},
            {l:'Above Appetite',v:activeRisks.filter(r=>r.appetiteThreshold&&r.residualLikelihood*r.residualImpact>r.appetiteThreshold).length,c:'border-amber-400',dot:'bg-amber-400'},
            {l:'Overdue Treatments',v:activeRisks.flatMap(r=>r.treatmentPlans||[]).filter(t=>t.dueDate&&new Date(t.dueDate)<new Date()&&t.status!=='Completed').length,c:'border-orange-400',dot:'bg-orange-400'}
          ].map((c,i)=>(
            <div key={i} className={`${glassPanel} border-l-4 ${c.c} p-5`}>
              <div className="flex items-center gap-2">
                <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-600">{c.l}</p>
                <span className={`w-3 h-3 rounded-full ${c.dot}`}/>
              </div>
              <p className="text-[52px] leading-none font-black text-slate-800 mt-4">{c.v}</p>
            </div>
          ))}
        </div>

        <div className={`${glassPanel} p-6`}>
          <h3 className={`${panelHeader} mb-5`}>Residual Risk Heatmap — Enterprise View</h3>
          <div className="max-w-[760px] mx-auto">
            <div className="grid grid-cols-6 gap-3 mb-3">
              <div className="text-right text-[11px] font-semibold text-slate-500 pr-2 flex items-center justify-end">Impact #</div>
              {[1,2,3,4,5].map(l=><div key={l} className="text-center text-[11px] font-semibold text-slate-500">L{l}</div>)}
            </div>
            {[5,4,3,2,1].map(i=>(
              <div key={i} className="grid grid-cols-6 gap-3 mb-3">
                <div className="text-right text-[11px] font-semibold text-slate-500 pr-2 flex items-center justify-end">#{i}</div>
                {[1,2,3,4,5].map(l=>{
                  const s=l*i;
                  const cr=activeRisks.filter(r=>r.residualLikelihood===l&&r.residualImpact===i);
                  const hc = heatColor(s);
                  return (
                    <div key={`${l}-${i}`} className={`${hc.bg} rounded-2xl h-[86px] flex flex-col items-center justify-center text-center shadow-sm`}>
                      {cr.length>0
                        ? <>
                            <span className={`text-3xl leading-none font-black ${hc.text}`}>{cr.length}</span>
                            <span className={`text-[11px] mt-1 ${hc.text} opacity-85`}>{hc.label||s}</span>
                          </>
                        : <span className={`text-lg font-semibold ${hc.text} opacity-50`}>{s}</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className={`${glassPanel} overflow-hidden`}>
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className={`${panelHeader}`}>Top 20 Risks — Ranked by Residual Score</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-slate-600">
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-[0.06em]">Ref</th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-[0.06em]">Risk</th>
                  <th className="px-4 py-3 text-center font-semibold uppercase tracking-[0.06em]">Tier</th>
                  <th className="px-4 py-3 text-center font-semibold uppercase tracking-[0.06em]">Inherent</th>
                  <th className="px-4 py-3 text-center font-semibold uppercase tracking-[0.06em]">Residual</th>
                  <th className="px-4 py-3 text-center font-semibold uppercase tracking-[0.06em]">Appetite</th>
                  <th className="px-4 py-3 text-center font-semibold uppercase tracking-[0.06em]">Velocity</th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-[0.06em]">Owner</th>
                  <th className="px-4 py-3 text-center font-semibold uppercase tracking-[0.06em]">Controls</th>
                </tr>
              </thead>
              <tbody>
                {activeRisks.sort((a,b)=>(b.residualLikelihood*b.residualImpact)-(a.residualLikelihood*a.residualImpact)).slice(0,20).map((r,idx)=>{
                  const ih=r.inherentLikelihood*r.inherentImpact;
                  const rs=r.residualLikelihood*r.residualImpact;
                  return (
                    <tr key={r.id} className={`border-t border-slate-100 ${idx%2===0?'bg-white':'bg-slate-50/70'}`}>
                      <td className="px-4 py-3 font-mono font-bold text-[#8f1024]">{r.reference}</td>
                      <td className="px-4 py-3 text-slate-800">{r.title}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${r.tier==='Strategic'?'bg-rose-100 text-rose-700':r.tier==='Project'?'bg-slate-100 text-slate-700':'bg-indigo-100 text-indigo-700'}`}>{r.tier}</span>
                      </td>
                      <td className="px-4 py-3 text-center"><span className={`px-2.5 py-1 rounded-full text-xs font-bold ${rlc(ih)}`}>{ih}</span></td>
                      <td className="px-4 py-3 text-center"><span className={`px-2.5 py-1 rounded-full text-xs font-bold ${rlc(rs)}`}>{rs}</span></td>
                      <td className="px-4 py-3 text-center text-slate-700">{r.appetiteThreshold??'—'}{r.isAboveAppetite&&<span className="ml-1 text-red-600">↑</span>}</td>
                      <td className="px-4 py-3 text-center text-slate-700">{r.velocity||'—'}</td>
                      <td className="px-4 py-3 text-slate-700">{r.riskOwner||'—'}</td>
                      <td className="px-4 py-3 text-center text-slate-700">{(r.controls||[]).length}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>)}


      {/* ═══════════ PORTFOLIO HEALTH & BALANCING (REBUILT V3.2) ═══════════ */}
      {view==='portfolio'&&(<div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-[28px] font-bold text-slate-800">Portfolio Health & Balancing</h3>
            <p className="text-[11px] text-slate-500 mt-1">Multi-dimensional view: composition, distribution, concentration, and rebalancing recommendations.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={pdfPortfolioReport} className={pdfBtn}><Printer size={14}/> PDF</button>
            <button onClick={wordPortfolioReport} className={btnS}><FileText size={14}/> Word</button>
            <button onClick={exportProjectReport} className={btnS}><FileSpreadsheet size={14}/> Excel</button>
          </div>
        </div>

        {/* Row 1: composition (kept from original) */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* By Project Type */}
          <div className={`${glassPanel} p-6`}>
            <h3 className={`${panelHeader} mb-5`}>By Project Type</h3>
            <div className="space-y-4">
              {['Research','Commercial','Contract'].map(t=>{
                const c=projects.filter(p=>p.projectType===t).length;
                const budget=projects.filter(p=>p.projectType===t).reduce((s,p)=>s+(p.totalBudget||0),0);
                const width = projects.length ? ((c/projects.length)*100) : 0;
                return (
                  <div key={t} className="flex items-center gap-4">
                    <span className="w-28 text-sm font-medium text-slate-700">{t}</span>
                    <div className="flex-1 h-4 rounded-full bg-slate-100 overflow-hidden shadow-inner">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#8f1024] to-[#be1732] shadow-[0_4px_10px_rgba(181,18,43,0.25)]" style={{width:`${width}%`}}/>
                    </div>
                    <span className="w-8 text-right text-lg font-bold text-slate-800">{c}</span>
                    <span className="w-16 text-right text-[11px] text-slate-500">{fmtR(budget)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* By Status */}
          <div className={`${glassPanel} p-6`}>
            <h3 className={`${panelHeader} mb-5`}>By Status</h3>
            <div className="space-y-4">
              {['Draft','Active','InProgress','Completed','OnHold','Cancelled'].map(s=>{
                const c=projects.filter(p=>p.status===s).length;
                const width = projects.length ? ((c/projects.length)*100) : 0;
                return c>0?(
                  <div key={s} className="flex items-center gap-4">
                    <span className="w-28 text-sm font-medium text-slate-700">{s}</span>
                    <div className="flex-1 h-4 rounded-full bg-slate-100 overflow-hidden shadow-inner">
                      <div className="h-full rounded-full bg-gradient-to-r from-emerald-300 to-emerald-500 shadow-[0_4px_12px_rgba(16,185,129,0.22)]" style={{width:`${width}%`}}/>
                    </div>
                    <span className="w-8 text-right text-lg font-bold text-slate-800">{c}</span>
                  </div>
                ):null;
              })}
            </div>
          </div>

          {/* Budget Utilisation */}
          <div className={`${glassPanel} p-6`}>
            <h3 className={`${panelHeader} mb-4`}>Budget Utilisation</h3>
            <div className="grid grid-cols-[150px,1fr] gap-5 items-center">
              <div className="flex items-center justify-center">
                <DonutChart percent={utilisation} size={128} strokeWidth={26} />
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-[40px] leading-none font-black text-slate-800">{utilisation.toFixed(1)}%</p>
                  <p className="text-sm text-slate-600 mt-1">Overall Utilization</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">Total Budget</p>
                    <p className="text-[22px] leading-none font-black text-slate-800 mt-2">{fmtR(totalBudget)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#8f1024]">Total Spent</p>
                    <p className="text-[22px] leading-none font-black text-[#8f1024] mt-2">{fmtR(totalSpent)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Strategic Alignment Matrix (bubble) */}
        <div className={`${glassPanel} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={`${panelHeader}`}>Strategic Alignment Matrix</h3>
              <p className="text-[11px] text-slate-500 mt-1">Strategic importance vs. residual risk · bubble size = budget. Sweet spot is high-importance, low-risk (top-left).</p>
            </div>
            <div className="text-[10px] text-slate-500 flex items-center gap-3">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-[#8f1024]"/>≥ R10M</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-[#c8102e]"/>R1M – R10M</span>
              <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-300"/>&lt; R1M</span>
            </div>
          </div>
          {(() => {
            // x-axis = strategic importance derived from priority (Critical=5, High=4, Medium=3, Low=2, none=1)
            // y-axis = average residual score across linked risks (0 if none)
            const priMap = { Critical: 5, High: 4, Medium: 3, Low: 2 };
            const W = 760, H = 320, PAD = 36;
            const xFor = (xVal) => PAD + ((xVal - 1) / 4) * (W - 2*PAD);
            const yFor = (yVal) => H - PAD - (Math.min(yVal,25) / 25) * (H - 2*PAD);
            const bubbles = activeProjects.map(p => {
              const x = priMap[p.priority] || 1;
              const linked = risks.filter(r => r.projectId === p.id);
              const y = linked.length
                ? linked.reduce((s,r)=>s+(r.residualLikelihood||0)*(r.residualImpact||0),0)/linked.length
                : 0;
              const r = (p.totalBudget||0) >= 10_000_000 ? 14 : (p.totalBudget||0) >= 1_000_000 ? 9 : 5;
              return { p, x, y, r };
            });
            return (
              <div className="overflow-x-auto">
                <svg width={W} height={H} className="block">
                  {/* gridlines */}
                  {[5,10,15,20,25].map(g => (
                    <g key={g}>
                      <line x1={PAD} y1={yFor(g)} x2={W-PAD} y2={yFor(g)} stroke="#e2e8f0" strokeDasharray="3,3"/>
                      <text x={PAD-6} y={yFor(g)+3} textAnchor="end" fontSize="9" fill="#94a3b8">{g}</text>
                    </g>
                  ))}
                  {[1,2,3,4,5].map(g => (
                    <g key={g}>
                      <line x1={xFor(g)} y1={PAD} x2={xFor(g)} y2={H-PAD} stroke="#f1f5f9"/>
                      <text x={xFor(g)} y={H-PAD+14} textAnchor="middle" fontSize="9" fill="#94a3b8">
                        {['','Low','Medium','High','Critical'][g-1] || 'Crit'}
                      </text>
                    </g>
                  ))}
                  {/* axes */}
                  <line x1={PAD} y1={H-PAD} x2={W-PAD} y2={H-PAD} stroke="#94a3b8"/>
                  <line x1={PAD} y1={PAD} x2={PAD} y2={H-PAD} stroke="#94a3b8"/>
                  <text x={W/2} y={H-4} textAnchor="middle" fontSize="10" fontWeight="600" fill="#64748b">Strategic Importance →</text>
                  <text x={12} y={H/2} textAnchor="middle" fontSize="10" fontWeight="600" fill="#64748b" transform={`rotate(-90 12 ${H/2})`}>Avg Residual Risk →</text>
                  {/* bubbles */}
                  {bubbles.map((b, i) => (
                    <g key={i}>
                      <circle cx={xFor(b.x)} cy={yFor(b.y)} r={b.r}
                              fill={b.r>=14?'#8f1024':b.r>=9?'#c8102e':'#fda4af'}
                              fillOpacity="0.65" stroke="#8f1024" strokeWidth="0.5">
                        <title>{b.p.reference} — {b.p.title}{'\n'}Budget: {fmtR(b.p.totalBudget||0)} · Avg risk: {b.y.toFixed(1)}</title>
                      </circle>
                      {b.r >= 9 && (
                        <text x={xFor(b.x)} y={yFor(b.y)-b.r-3} textAnchor="middle" fontSize="8" fill="#475569" fontWeight="700">
                          {b.p.reference}
                        </text>
                      )}
                    </g>
                  ))}
                </svg>
              </div>
            );
          })()}
        </div>

        {/* Row 3: TRL Stage Distribution + Funding-Source Mix + Division Balance */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* TRL Stage Distribution */}
          <div className={`${glassPanel} p-6`}>
            <h3 className={`${panelHeader} mb-5`}>TRL Stage Distribution</h3>
            <p className="text-[11px] text-slate-500 mb-3">Pipeline maturity — balance between basic research (TRL 1-3), development (TRL 4-6), and deployment (TRL 7-9).</p>
            <div className="space-y-1.5">
              {[1,2,3,4,5,6,7,8,9].map(stage => {
                const c = projects.filter(p => (p.trlStage||0) === stage).length;
                const max = Math.max(1, ...[1,2,3,4,5,6,7,8,9].map(s => projects.filter(p => (p.trlStage||0) === s).length));
                const width = (c / max) * 100;
                const band = stage <= 3 ? 'bg-amber-400' : stage <= 6 ? 'bg-blue-400' : 'bg-emerald-500';
                return (
                  <div key={stage} className="flex items-center gap-2 text-[11px]">
                    <span className="w-12 text-slate-600 font-mono">TRL {stage}</span>
                    <div className="flex-1 h-4 rounded bg-slate-100 overflow-hidden">
                      {c > 0 && <div className={`h-full ${band}`} style={{width:`${width}%`}}/>}
                    </div>
                    <span className="w-6 text-right font-bold text-slate-700">{c}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-4 mt-3 text-[10px] text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-400"/>Basic (1-3)</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-blue-400"/>Dev (4-6)</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-500"/>Deploy (7-9)</span>
            </div>
          </div>

          {/* Funding Source Mix */}
          <div className={`${glassPanel} p-6`}>
            <h3 className={`${panelHeader} mb-5`}>Funding Source Mix</h3>
            <p className="text-[11px] text-slate-500 mb-3">Diversification reduces external concentration risk.</p>
            {(() => {
              const sourceMap = {};
              projects.forEach(p => {
                const s = p.fundingSource || 'Unspecified';
                sourceMap[s] = (sourceMap[s]||0) + (p.totalBudget||0);
              });
              const total = Object.values(sourceMap).reduce((s,v)=>s+v,0) || 1;
              const entries = Object.entries(sourceMap).sort((a,b)=>b[1]-a[1]);
              const palette = ['#8f1024','#c8102e','#f59e0b','#10b981','#6366f1','#94a3b8'];
              return (
                <>
                  {/* stacked bar */}
                  <div className="flex h-5 rounded-full overflow-hidden bg-slate-100 mb-3 shadow-inner">
                    {entries.map(([k,v], i) => (
                      <div key={k} className="h-full" title={`${k}: ${fmtR(v)}`}
                           style={{width:`${(v/total)*100}%`,background:palette[i%palette.length]}}/>
                    ))}
                  </div>
                  <div className="space-y-1.5">
                    {entries.map(([k,v], i) => (
                      <div key={k} className="flex items-center gap-2 text-[11px]">
                        <span className="w-2.5 h-2.5 rounded shrink-0" style={{background:palette[i%palette.length]}}/>
                        <span className="flex-1 text-slate-700 truncate">{k}</span>
                        <span className="font-bold text-slate-800">{fmtR(v)}</span>
                        <span className="w-10 text-right text-slate-400">{((v/total)*100).toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Division Balance */}
          <div className={`${glassPanel} p-6`}>
            <h3 className={`${panelHeader} mb-5`}>Division Balance</h3>
            <p className="text-[11px] text-slate-500 mb-3">Active projects per division vs. budget allocated.</p>
            <div className="space-y-2.5">
              {divisions.map(div => {
                const divProjects = projects.filter(p => p.division?.id === div.id || p.divisionId === div.id);
                const divBudget = divProjects.reduce((s,p)=>s+(p.totalBudget||0),0);
                const maxBudget = Math.max(1, ...divisions.map(d => projects.filter(p => p.division?.id===d.id||p.divisionId===d.id).reduce((s,p)=>s+(p.totalBudget||0),0)));
                const width = (divBudget / maxBudget) * 100;
                if (divProjects.length === 0) return null;
                return (
                  <div key={div.id} className="flex items-center gap-2 text-[11px]">
                    <span className="w-28 text-slate-700 truncate" title={div.name}>{div.name}</span>
                    <div className="flex-1 h-3.5 rounded bg-slate-100 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#8f1024] to-[#c8102e]" style={{width:`${width}%`}}/>
                    </div>
                    <span className="w-8 text-center font-bold text-slate-700">{divProjects.length}</span>
                    <span className="w-16 text-right text-slate-500">{fmtR(divBudget)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Row 4: Rebalancing Recommendations */}
        <div className={`${glassPanel} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={`${panelHeader}`}>Rebalancing Recommendations</h3>
              <p className="text-[11px] text-slate-500 mt-1">Rules-based analysis of portfolio composition. Each recommendation explains the rule that triggered it.</p>
            </div>
            <span className="px-3 py-1 rounded-full bg-slate-100 text-[10px] font-bold text-slate-600">{balancingRecommendations.length} finding{balancingRecommendations.length===1?'':'s'}</span>
          </div>
          {balancingRecommendations.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 size={32} className="text-emerald-500 mx-auto mb-2"/>
              <p className="text-sm text-slate-600 font-semibold">Portfolio is well balanced</p>
              <p className="text-[11px] text-slate-400 mt-1">No rebalancing recommendations were triggered by current data.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {balancingRecommendations.map((r, i) => {
                const sev = r.severity === 'high'
                  ? { border:'border-l-[#8f1024]', bg:'bg-rose-50', dot:'bg-[#8f1024]', label:'High' }
                  : r.severity === 'medium'
                  ? { border:'border-l-amber-500', bg:'bg-amber-50', dot:'bg-amber-500', label:'Medium' }
                  : { border:'border-l-blue-400',   bg:'bg-blue-50',  dot:'bg-blue-400',  label:'Low' };
                return (
                  <div key={i} className={`rounded-xl border border-slate-200 border-l-4 ${sev.border} ${sev.bg} px-4 py-3`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full ${sev.dot}`}/>
                      <span className="text-sm font-bold text-slate-800">{r.title}</span>
                      <span className="ml-auto text-[10px] uppercase font-bold text-slate-500">{sev.label}</span>
                    </div>
                    <p className="text-[12px] text-slate-700 leading-snug">{r.body}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Row 5: At-Risk Projects (kept) */}
        <div className={`${glassPanel} overflow-hidden`}>
          <div className="px-6 py-5 border-b border-slate-200 bg-[#f7f7f3]">
            <h3 className="text-[20px] leading-none font-bold uppercase tracking-tight text-slate-800">Projects Requiring Attention</h3>
            <p className="text-[11px] text-slate-500 mt-1">Active projects with progress under 40%, sorted by lowest first.</p>
          </div>
          <div className="overflow-x-auto px-6 py-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-slate-700">
                  <th className="px-3 py-3 text-left font-semibold uppercase tracking-[0.06em]">Ref</th>
                  <th className="px-3 py-3 text-left font-semibold uppercase tracking-[0.06em]">Project</th>
                  <th className="px-3 py-3 text-center font-semibold uppercase tracking-[0.06em]">Type</th>
                  <th className="px-3 py-3 text-center font-semibold uppercase tracking-[0.06em]">Progress</th>
                  <th className="px-3 py-3 text-center font-semibold uppercase tracking-[0.06em]">Priority</th>
                  <th className="px-3 py-3 text-right font-semibold uppercase tracking-[0.06em]">Budget</th>
                  <th className="px-3 py-3 text-right font-semibold uppercase tracking-[0.06em]">Spent</th>
                </tr>
              </thead>
              <tbody>
                {activeProjects.filter(p=>p.progressPercentage<40).sort((a,b)=>a.progressPercentage-b.progressPercentage).slice(0,10).map((p,idx)=>(
                  <tr key={p.id} className={`border-t border-slate-100 ${idx%2===1?'bg-slate-50/70':''}`}>
                    <td className="px-3 py-3 font-mono text-[13px] text-[#8f1024] font-bold">{p.reference}</td>
                    <td className="px-3 py-3 text-slate-800">{p.title}</td>
                    <td className="px-3 py-3 text-center text-slate-700">{p.projectType}</td>
                    <td className="px-3 py-3 text-center font-bold text-[#8f1024]">{p.progressPercentage}%</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        p.priority==='Critical'?'bg-red-100 text-red-700':
                        p.priority==='High'?'bg-amber-100 text-amber-700':
                        p.priority==='Medium'?'bg-rose-50 text-rose-700':'bg-slate-100 text-slate-600'
                      }`}>{p.priority}</span>
                    </td>
                    <td className="px-3 py-3 text-right font-medium text-slate-800">{fmtR(p.totalBudget)}</td>
                    <td className="px-3 py-3 text-right font-medium text-[#8f1024]">{p.actualSpend ? fmtR(p.actualSpend) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>)}


      {/* ═══════════ FORECASTING & PROJECTIONS (NEW V3.2) ═══════════ */}
      {view==='forecasting'&&(<div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-[28px] font-bold text-slate-800">Forecasting & Projections</h3>
            <p className="text-[11px] text-slate-500 mt-1">Year-end and completion forecasts based on actuals to date. All projections use ordinary-least-squares regression — fully explainable, no machine learning.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={pdfForecastReport} className={pdfBtn}><Printer size={14}/> PDF</button>
            <button onClick={wordForecastReport} className={btnS}><FileText size={14}/> Word</button>
          </div>
        </div>

        {/* Summary stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className={`${glassPanel} border-l-4 border-emerald-500 p-4`}>
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-emerald-700">On Track</p>
            <p className="text-[32px] leading-none font-black text-slate-800 mt-2">{kpiForecasts.filter(f=>f.status==='On Track').length}</p>
            <p className="text-[10px] text-slate-500 mt-1">KPIs projected to meet target</p>
          </div>
          <div className={`${glassPanel} border-l-4 border-amber-500 p-4`}>
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-amber-700">At Risk</p>
            <p className="text-[32px] leading-none font-black text-slate-800 mt-2">{kpiForecasts.filter(f=>f.status==='At Risk').length}</p>
            <p className="text-[10px] text-slate-500 mt-1">KPIs trending −15% or less</p>
          </div>
          <div className={`${glassPanel} border-l-4 border-[#8f1024] p-4`}>
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#8f1024]">Likely Miss</p>
            <p className="text-[32px] leading-none font-black text-slate-800 mt-2">{kpiForecasts.filter(f=>f.status==='Likely Miss').length}</p>
            <p className="text-[10px] text-slate-500 mt-1">Material gap projected</p>
          </div>
          <div className={`${glassPanel} border-l-4 border-orange-400 p-4`}>
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-orange-700">Over Budget</p>
            <p className="text-[32px] leading-none font-black text-slate-800 mt-2">{budgetForecasts.filter(f=>f.status==='Over Budget').length}</p>
            <p className="text-[10px] text-slate-500 mt-1">Projects with burn over budget</p>
          </div>
        </div>

        {/* KPI year-end projection */}
        <div className={`${glassPanel} overflow-hidden`}>
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className={`${panelHeader}`}>KPI Year-End Projection</h3>
            <p className="text-[11px] text-slate-500 mt-1">Linear regression on quarterly actuals. Missing quarters are predicted using the fit line.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-600 text-[11px]">
                  <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Ref</th>
                  <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">KPI</th>
                  <th className="px-3 py-2.5 text-right font-semibold uppercase tracking-wider">Annual Target</th>
                  <th className="px-3 py-2.5 text-center font-semibold uppercase tracking-wider">Data Points</th>
                  <th className="px-3 py-2.5 text-right font-semibold uppercase tracking-wider">Projected Annual</th>
                  <th className="px-3 py-2.5 text-right font-semibold uppercase tracking-wider">Variance</th>
                  <th className="px-3 py-2.5 text-center font-semibold uppercase tracking-wider">Confidence</th>
                  <th className="px-3 py-2.5 text-center font-semibold uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {kpiForecasts.map((f,idx) => {
                  const v = f.variance;
                  const tones = {
                    'On Track':    'bg-emerald-100 text-emerald-700',
                    'At Risk':     'bg-amber-100 text-amber-700',
                    'Likely Miss': 'bg-red-100 text-red-700',
                    'No data':     'bg-slate-100 text-slate-500'
                  };
                  const confTones = { Insufficient:'slate', Low:'amber', Moderate:'blue', High:'emerald' };
                  const cT = confTones[f.confidence.label] || 'slate';
                  return (
                    <tr key={f.kpiId} className={`border-t border-slate-100 ${idx%2===1?'bg-slate-50/40':''}`}>
                      <td className="px-3 py-2.5 font-mono text-[#8f1024] font-bold text-[11px]">{f.reference}</td>
                      <td className="px-3 py-2.5 text-slate-800 max-w-[280px] truncate">{f.name}</td>
                      <td className="px-3 py-2.5 text-right text-slate-700">{f.annualTarget||'—'}</td>
                      <td className="px-3 py-2.5 text-center text-slate-500">{f.knownActuals}/4</td>
                      <td className="px-3 py-2.5 text-right font-bold text-slate-800">{f.projectedAnnual!=null ? Math.round(f.projectedAnnual*100)/100 : '—'}</td>
                      <td className={`px-3 py-2.5 text-right font-bold ${v==null?'text-slate-400':v>=0?'text-emerald-600':v>=-15?'text-amber-600':'text-red-600'}`}>
                        {v!=null ? `${v>=0?'+':''}${v.toFixed(1)}%` : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold bg-${cT}-100 text-${cT}-700`}>{f.confidence.label}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${tones[f.status]||tones['No data']}`}>{f.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Budget burn-rate forecast */}
        <div className={`${glassPanel} overflow-hidden`}>
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className={`${panelHeader}`}>Budget Burn-Rate Forecast</h3>
            <p className="text-[11px] text-slate-500 mt-1">Monthly burn × total project duration. Compares forecast final spend to allocated budget.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-600 text-[11px]">
                  <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Ref</th>
                  <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Project</th>
                  <th className="px-3 py-2.5 text-right font-semibold uppercase tracking-wider">Budget</th>
                  <th className="px-3 py-2.5 text-right font-semibold uppercase tracking-wider">Spent</th>
                  <th className="px-3 py-2.5 text-right font-semibold uppercase tracking-wider">Monthly Burn</th>
                  <th className="px-3 py-2.5 text-right font-semibold uppercase tracking-wider">Forecast Final</th>
                  <th className="px-3 py-2.5 text-right font-semibold uppercase tracking-wider">Variance</th>
                  <th className="px-3 py-2.5 text-center font-semibold uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {budgetForecasts.map((f, idx) => {
                  const v = f.variance;
                  const tone = f.status === 'On Budget'   ? 'bg-emerald-100 text-emerald-700'
                             : f.status === 'At Risk'      ? 'bg-amber-100 text-amber-700'
                             : f.status === 'Over Budget'  ? 'bg-red-100 text-red-700'
                             : 'bg-slate-100 text-slate-500';
                  return (
                    <tr key={f.reference+idx} className={`border-t border-slate-100 ${idx%2===1?'bg-slate-50/40':''}`}>
                      <td className="px-3 py-2.5 font-mono text-[#8f1024] font-bold text-[11px]">{f.reference}</td>
                      <td className="px-3 py-2.5 text-slate-800 max-w-[260px] truncate">{f.title}</td>
                      <td className="px-3 py-2.5 text-right text-slate-700">{fmtR(f.budget)}</td>
                      <td className="px-3 py-2.5 text-right text-slate-700">{fmtR(f.spent)}</td>
                      <td className="px-3 py-2.5 text-right text-slate-700">{f.monthlyBurn!=null ? fmtR(f.monthlyBurn) : '—'}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-slate-800">{f.forecastFinalSpend!=null ? fmtR(f.forecastFinalSpend) : '—'}</td>
                      <td className={`px-3 py-2.5 text-right font-bold ${v==null?'text-slate-400':v<=0?'text-emerald-600':v<=10?'text-amber-600':'text-red-600'}`}>
                        {v!=null ? `${v>=0?'+':''}${v.toFixed(1)}%` : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-center"><span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${tone}`}>{f.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Project completion forecast */}
        <div className={`${glassPanel} overflow-hidden`}>
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className={`${panelHeader}`}>Project Completion Forecast</h3>
            <p className="text-[11px] text-slate-500 mt-1">Velocity = current progress ÷ months elapsed. Forecast end date = today + (100 − progress) ÷ velocity months.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-600 text-[11px]">
                  <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Ref</th>
                  <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Project</th>
                  <th className="px-3 py-2.5 text-center font-semibold uppercase tracking-wider">Progress</th>
                  <th className="px-3 py-2.5 text-center font-semibold uppercase tracking-wider">Velocity (%/mo)</th>
                  <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Planned End</th>
                  <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Forecast End</th>
                  <th className="px-3 py-2.5 text-right font-semibold uppercase tracking-wider">Slip</th>
                  <th className="px-3 py-2.5 text-center font-semibold uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {completionForecasts.map((f, idx) => {
                  const tone = f.status === 'On Schedule' || f.status === 'Completed' ? 'bg-emerald-100 text-emerald-700'
                             : f.status === 'Minor Slip'           ? 'bg-amber-100 text-amber-700'
                             : f.status === 'At Risk'              ? 'bg-orange-100 text-orange-700'
                             : f.status === 'Significant Slip'     ? 'bg-red-100 text-red-700'
                             : 'bg-slate-100 text-slate-500';
                  return (
                    <tr key={f.reference+idx} className={`border-t border-slate-100 ${idx%2===1?'bg-slate-50/40':''}`}>
                      <td className="px-3 py-2.5 font-mono text-[#8f1024] font-bold text-[11px]">{f.reference}</td>
                      <td className="px-3 py-2.5 text-slate-800 max-w-[240px] truncate">{f.title}</td>
                      <td className="px-3 py-2.5 text-center font-bold text-slate-700">{f.progress}%</td>
                      <td className="px-3 py-2.5 text-center text-slate-700">{f.velocity!=null ? f.velocity : '—'}</td>
                      <td className="px-3 py-2.5 text-slate-700 text-[11px]">{f.plannedEnd ? fmtDate(f.plannedEnd) : '—'}</td>
                      <td className="px-3 py-2.5 text-slate-800 text-[11px] font-medium">{f.forecastEnd ? fmtDate(f.forecastEnd) : '—'}</td>
                      <td className={`px-3 py-2.5 text-right font-bold ${f.slipDays==null?'text-slate-400':f.slipDays<=0?'text-emerald-600':f.slipDays<=30?'text-amber-600':'text-red-600'}`}>
                        {f.slipDays!=null ? `${f.slipDays>0?'+':''}${f.slipDays}d` : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-center"><span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${tone}`}>{f.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Risk trend direction */}
        <div className={`${glassPanel} overflow-hidden`}>
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className={`${panelHeader}`}>Risk Trend Direction</h3>
            <p className="text-[11px] text-slate-500 mt-1">Direction of residual-score movement over recorded risk reviews. Stable risks have a slope between −0.005 and +0.005 per day.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
            {['Improving','Stable','Worsening'].map(direction => {
              const matches = riskTrends.filter(r => r.direction === direction);
              const Icon = direction === 'Improving' ? TrendingDown : direction === 'Worsening' ? TrendingUp : Activity;
              const tone = direction === 'Improving' ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                         : direction === 'Worsening' ? 'text-red-700 bg-red-50 border-red-200'
                         : 'text-slate-700 bg-slate-50 border-slate-200';
              return (
                <div key={direction} className={`rounded-2xl border ${tone} p-4`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon size={18}/>
                    <span className="text-xs font-bold uppercase tracking-wider">{direction}</span>
                    <span className="ml-auto text-2xl font-black">{matches.length}</span>
                  </div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {matches.slice(0,8).map(r => (
                      <div key={r.reference} className="flex items-center gap-2 text-[11px] py-1 border-t border-current border-opacity-20 first:border-t-0">
                        <span className="font-mono font-bold opacity-70">{r.reference}</span>
                        <span className="flex-1 truncate opacity-90">{r.title}</span>
                        <span className="font-bold">{r.currentScore}</span>
                      </div>
                    ))}
                    {matches.length === 0 && <p className="text-[11px] opacity-60 italic py-1">None</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Methodology footer */}
        <div className={`${glassPanel} bg-slate-50 p-5`}>
          <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-700 mb-2">Methodology</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-[11px] text-slate-600 leading-relaxed">
            <p><b>KPI Year-End Projection:</b> ordinary-least-squares regression through recorded quarterly actuals. Missing quarters are predicted by the fit line; annual = sum of all four.</p>
            <p><b>Budget Burn-Rate Forecast:</b> spent-to-date ÷ months elapsed = monthly burn. Forecast final = burn × total months in project duration.</p>
            <p><b>Project Completion Forecast:</b> progress% ÷ months elapsed = velocity. Forecast end = today + (100 − progress) ÷ velocity months.</p>
            <p><b>Confidence:</b> qualitative band based on number of data points and R². Low &lt; 3 points or R² &lt; 0.3 · High ≥ 4 points and R² ≥ 0.8.</p>
          </div>
        </div>
      </div>)}


      {/* ═══════════ REPORT GENERATOR (Image 6) ═══════════ */}
      {view==='reports'&&(<div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-[28px] font-bold text-slate-800">Report Generator</h3>
            <p className="text-[11px] text-slate-500 mt-1">Statutory templates &middot; quick data exports &middot; user-defined custom reports.</p>
          </div>
          <button onClick={()=>openBuilder(null)} className={`${pdfBtn} !bg-[#c8102e] hover:!bg-[#a00d24]`}>
            <Wand2 size={14}/> Custom Report Builder
          </button>
        </div>

        {/* ── Saved Templates panel (V3.2 — Custom Report Builder output) ── */}
        <div className={`${glassPanel} p-5`}>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-[#8f1024]"/>
              <h3 className={`${panelHeader}`}>Saved Report Templates ({templates.length})</h3>
            </div>
            <p className="text-[10px] text-slate-400">Reusable configurations created via the Custom Report Builder. Shared templates are visible to everyone.</p>
          </div>

          {templates.length === 0 ? (
            <div className="text-center py-8">
              <FileText size={32} className="text-slate-300 mx-auto mb-2"/>
              <p className="text-sm text-slate-500 font-semibold">No saved templates yet</p>
              <p className="text-[11px] text-slate-400 mt-1">Click <b>Custom Report Builder</b> above to design your first one.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {templates.map(t => {
                const fmtIcon = t.outputFormat==='Word' ? FileText : t.outputFormat==='Excel' ? FileSpreadsheet : Printer;
                const FmtIcon = fmtIcon;
                const owned = t.createdBy === (localStorage.getItem('silogate_user') ? JSON.parse(localStorage.getItem('silogate_user')||'{}').username : null) || !t.isShared;
                return (
                  <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-slate-800 truncate" title={t.name}>{t.name}</p>
                        {t.description && <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{t.description}</p>}
                      </div>
                      {t.isShared
                        ? <Share2 size={12} className="text-blue-500 mt-1 shrink-0" title="Shared template"/>
                        : <Lock size={12} className="text-slate-400 mt-1 shrink-0" title="Private template"/>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap text-[10px] mb-3">
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold">{t.dataSource}</span>
                      <span className="px-2 py-0.5 rounded-full bg-[#fff3f5] text-[#8f1024] font-semibold flex items-center gap-1">
                        <FmtIcon size={9}/>{t.outputFormat}
                      </span>
                      {t.runCount > 0 && <span className="text-slate-400">Run {t.runCount}×</span>}
                      {t.lastRunAt && <span className="text-slate-400">&middot; {fmtDate(t.lastRunAt)}</span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={()=>runCustomReport(t)} className="flex-1 px-3 py-1.5 rounded-lg bg-[#c8102e] text-white text-[11px] font-semibold hover:bg-[#a00d24] flex items-center justify-center gap-1">
                        <Play size={11}/> Run
                      </button>
                      <button onClick={()=>openBuilder(t)} className="px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200" title="Edit"><Edit3 size={11}/></button>
                      <button onClick={()=>deleteTemplate(t.id)} className="px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-red-100 hover:text-red-700" title="Delete"><Trash2 size={11}/></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mt-2">Statutory & Governance Reports</h3>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className={`${glassPanel} p-5`}>
            <h3 className={`${panelHeader} mb-4`}>APP Reports</h3>
            <div className="grid grid-cols-2 gap-3">
              {QS.map(q=>(
                <button key={q} onClick={()=>generateReport('APP_Quarterly',q)} className="rounded-2xl border border-slate-200 bg-white shadow-sm px-4 py-6 text-center hover:bg-slate-50 transition">
                  <p className="text-3xl leading-none font-black text-slate-800">{q}</p>
                  <p className="text-[11px] text-slate-400 mt-2">Quarterly APP</p>
                </button>
              ))}
              <button onClick={()=>generateReport('APP_Annual',null)} className="rounded-2xl border border-[#b5122b]/30 bg-[#fff7f8] shadow-sm px-4 py-6 text-center hover:bg-[#fff1f4] transition">
                <p className="text-xl leading-none font-black text-[#8f1024]">Annual APP</p>
                <p className="text-[11px] text-slate-400 mt-2">Full Year Report</p>
              </button>
              <button onClick={()=>generateReport('State_Grant',null)} className="rounded-2xl border border-slate-200 bg-white shadow-sm px-4 py-6 text-center hover:bg-slate-50 transition">
                <p className="text-xl leading-none font-black text-slate-700">State Grant</p>
                <p className="text-[11px] text-slate-400 mt-2">Investment Report</p>
              </button>
            </div>
          </div>

          <div className={`${glassPanel} p-5`}>
            <h3 className={`${panelHeader} mb-4`}>Governance Reports</h3>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={()=>generateReport('Board_Risk',null)} className="rounded-2xl border border-slate-200 bg-white shadow-sm px-4 py-5 text-center hover:bg-slate-50 transition">
                <p className="text-sm font-bold text-slate-800">Board Risk Report</p>
              </button>
              <button onClick={()=>generateReport('Board_Performance',null)} className="rounded-2xl border border-slate-200 bg-white shadow-sm px-4 py-5 text-center hover:bg-slate-50 transition">
                <p className="text-sm font-bold text-slate-800">Board Performance</p>
              </button>
              <button onClick={()=>generateReport('Parliamentary_QA',null)} className="rounded-2xl border border-slate-200 bg-white shadow-sm px-4 py-5 text-center hover:bg-slate-50 transition">
                <p className="text-sm font-bold text-slate-800">Parliamentary Q&amp;A</p>
              </button>
              <button onClick={()=>generateReport('Divisional',null)} className="rounded-2xl border border-slate-200 bg-white shadow-sm px-4 py-5 text-center hover:bg-slate-50 transition">
                <p className="text-sm font-bold text-slate-800">Divisional Report</p>
              </button>
            </div>
          </div>
        </div>

        <div className={`${glassPanel} p-5`}>
          <h3 className={`${panelHeader} mb-4`}>Quick Data Exports</h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <button onClick={exportKpiReport} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-5 text-left shadow-sm hover:bg-slate-50 transition">
              <div className="w-10 h-10 rounded-xl bg-[#fff3f5] flex items-center justify-center shrink-0"><BarChart3 size={18} className="text-[#8f1024]"/></div>
              <div>
                <p className="text-sm font-bold text-slate-800">APP KPI Performance</p>
                <p className="text-[11px] text-slate-400">All KPIs with quarterly actuals &amp; variances (.csv)</p>
              </div>
            </button>
            <button onClick={exportRiskReport} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-5 text-left shadow-sm hover:bg-slate-50 transition">
              <div className="w-10 h-10 rounded-xl bg-[#fff3f5] flex items-center justify-center shrink-0"><ShieldCheck size={18} className="text-[#8f1024]"/></div>
              <div>
                <p className="text-sm font-bold text-slate-800">Enterprise Risk Register</p>
                <p className="text-[11px] text-slate-400">All risks with scoring, owners &amp; controls (.csv)</p>
              </div>
            </button>
            <button onClick={exportProjectReport} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-5 text-left shadow-sm hover:bg-slate-50 transition">
              <div className="w-10 h-10 rounded-xl bg-[#fff3f5] flex items-center justify-center shrink-0"><FolderKanban size={18} className="text-[#8f1024]"/></div>
              <div>
                <p className="text-sm font-bold text-slate-800">Project Portfolio</p>
                <p className="text-[11px] text-slate-400">All projects with budget &amp; progress (.csv)</p>
              </div>
            </button>
          </div>
        </div>

        <div className={`${glassPanel} p-5`}>
          <h3 className={`${panelHeader} mb-4`}>Generated Reports ({reports.length})</h3>
          {reports.length>0
            ? <div className="space-y-2">
                {reports.map((r,idx)=>(
                  <div key={r.id} className={`flex items-center gap-3 rounded-xl px-4 py-3 ${idx%2===0?'bg-slate-50':'bg-white'}`}>
                    <div className="w-8 h-8 rounded-lg bg-[#fff3f5] flex items-center justify-center shrink-0"><FileSpreadsheet size={16} className="text-[#8f1024]"/></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{r.title}</p>
                      <p className="text-[11px] text-slate-400">{r.reportType} · Generated {fmtDate(r.generatedAt)}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-[10px] font-semibold ${approvalBadge(r.approvalStatus)}`}>{r.approvalStatus||'Pending'}</span>
                  </div>
                ))}
              </div>
            : <p className="text-sm text-slate-400 text-center py-6">No reports generated yet — use the options above.</p>}
        </div>
      </div>)}


      {/* ═══════════ APPROVALS & WORKFLOWS ═══════════ */}
      {view==='workflows'&&(() => {
        const pill = 'inline-flex items-center px-3 py-1 rounded-full text-[10px] font-semibold border border-[#b5122b]/40 bg-[#fff5f7] text-[#8f1024] shrink-0';
        const pillGreen = 'inline-flex items-center px-3 py-1 rounded-full text-[10px] font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700';
        const pillRej   = 'inline-flex items-center px-3 py-1 rounded-full text-[10px] font-semibold border border-slate-300 bg-white text-slate-700';
        const row = 'flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0';

        const kpiActualsPending  = kpis.flatMap(k=>(k.actuals||[]).filter(a=>a.approvalStatus==='Pending').map(a=>({...a,kpiRef:k.reference,kpiName:k.name})));
        const kpiActualsApproved = kpis.flatMap(k=>(k.actuals||[]).filter(a=>a.approvalStatus==='Approved'));
        const kpiActualsRejected = kpis.flatMap(k=>(k.actuals||[]).filter(a=>a.approvalStatus==='Rejected'));
        const gatesPending  = projects.flatMap(p=>(p.stageGates||[]).filter(sg=>sg.approvalStatus==='Pending').map(sg=>({...sg,projRef:p.reference,projTitle:p.title})));
        const gatesApproved = projects.flatMap(p=>(p.stageGates||[]).filter(sg=>sg.approvalStatus==='Approved'));
        const gatesRejected = projects.flatMap(p=>(p.stageGates||[]).filter(sg=>sg.approvalStatus==='Rejected'));
        const reportsPending  = reports.filter(r=>r.approvalStatus==='Pending');
        const reportsApproved = reports.filter(r=>r.approvalStatus==='Approved');
        const reportsRejected = reports.filter(r=>r.approvalStatus==='Rejected');

        const slaRows = [
          {l:'KPI Gate Submissions', pending:kpiActualsPending.length, approved:kpiActualsApproved.length, rejected:kpiActualsRejected.length},
          {l:'Stage Gate Reviews',   pending:gatesPending.length,       approved:gatesApproved.length,       rejected:gatesRejected.length},
          {l:'Report Sign-off',      pending:reportsPending.length,     approved:reportsApproved.length,     rejected:reportsRejected.length}
        ];

        return (
        <div className="space-y-6">
          <div className={`${glassPanel} bg-slate-50/70 px-5 py-4`}>
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
              <span className="font-semibold text-slate-700 mr-1">About status badges below</span>
              <span className={pill}>Pending Approval</span>
              <span>means the item has been submitted by the data owner and is awaiting sign-off from the relevant approver.</span>
              <span className={pillGreen}>Approved</span>
              <span>or</span>
              <span className={pillRej}>Rejected</span>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className={`${glassPanel} p-5`}>
              <h3 className={`${panelHeader} mb-4`}>KPI Actuals — Pending Approval</h3>
              <div className="space-y-0">
                {kpiActualsPending.slice(0,10).map(a => (
                  <div key={a.id} className={row}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{a.kpiRef} — {a.kpiName}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{a.quarter} FY{a.financialYear} · Actual: {a.actualValue} · Submitted {fmtDate(a.submittedAt)}</p>
                    </div>
                    <span className={pill}>Pending Approval</span>
                  </div>
                ))}
                {kpiActualsPending.length===0 && <p className="text-sm text-slate-400 text-center py-8">No pending approvals</p>}
              </div>
            </div>

            <div className={`${glassPanel} p-5`}>
              <h3 className={`${panelHeader} mb-4`}>Stage Gates — Pending Review</h3>
              <div className="space-y-0">
                {gatesPending.slice(0,10).map(sg => (
                  <div key={sg.id} className={row}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{sg.projRef} — {sg.projTitle}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">Gate: {sg.stageName} · Due {fmtDate(sg.dueDate)}</p>
                    </div>
                    <span className={pill}>Pending Review</span>
                  </div>
                ))}
                {gatesPending.length===0 && <p className="text-sm text-slate-400 text-center py-16">No pending gates</p>}
              </div>
            </div>

            <div className={`${glassPanel} p-5`}>
              <h3 className={`${panelHeader} mb-4`}>Reports — Pending Sign-off</h3>
              <div className="space-y-0">
                {reportsPending.map(r => (
                  <div key={r.id} className={row}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{r.title}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{r.reportType} · {fmtDate(r.generatedAt)}</p>
                    </div>
                    <span className={pill}>Pending Sign-off</span>
                  </div>
                ))}
                {reportsPending.length===0 && <p className="text-sm text-slate-400 text-center py-8">No pending report sign-offs</p>}
              </div>
            </div>

            <div className={`${glassPanel} p-5`}>
              <h3 className={`${panelHeader} mb-5`}>Approval SLA Summary</h3>
              <div className="space-y-4">
                {slaRows.map(wf => {
                  const total = (wf.approved + wf.pending + wf.rejected) || 1;
                  return (
                    <div key={wf.l} className="flex items-center gap-4">
                      <span className="w-40 shrink-0 text-sm text-slate-700">{wf.l}</span>
                      <div className="flex-1 flex h-4 rounded-full overflow-hidden bg-slate-100 shadow-inner">
                        {wf.approved>0 && <div title={`${wf.approved} approved`} className="bg-emerald-500" style={{width:`${(wf.approved/total)*100}%`}}/>}
                        {wf.pending >0 && <div title={`${wf.pending} pending approval`} className="bg-amber-400" style={{width:`${(wf.pending/total)*100}%`}}/>}
                        {wf.rejected>0 && <div title={`${wf.rejected} rejected`} className="bg-[#b5122b]" style={{width:`${(wf.rejected/total)*100}%`}}/>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-[11px] text-slate-500">
                <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500"/>Approved</span>
                <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-amber-400"/>Pending Approval</span>
                <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-[#b5122b]"/>Rejected</span>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ═══════════ CUSTOM REPORT BUILDER MODAL (V3.2) ═══════════ */}
      <Modal open={builderOpen && !!draftTemplate} wide onClose={()=>{setBuilderOpen(false); setEditingTemplate(null); setDraftTemplate(null);}} title={editingTemplate ? `Edit Template — ${editingTemplate.name}` : 'Custom Report Builder'}>
        {draftTemplate && (() => {
          const d = draftTemplate;
          const setD = (patch) => setDraftTemplate({...d, ...patch});
          const setFilter = (k, v) => setD({ filters: { ...(d.filters||{}), [k]: v } });
          const catalogue = FIELD_CATALOGUE[d.dataSource] || [];
          const toggleField = (k) => {
            const has = d.fields.includes(k);
            setD({ fields: has ? d.fields.filter(f=>f!==k) : [...d.fields, k] });
          };
          const { count: previewCount } = materialiseCustomReport({
            dataSource: d.dataSource, fields: d.fields, filters: d.filters,
            sortBy: d.sortBy, sortOrder: d.sortOrder
          });
          const labelCls = 'text-[11px] font-bold uppercase tracking-[0.06em] text-slate-600 mb-1.5 block';

          return (
            <div className="space-y-5">
              {/* Name + Description */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className={labelCls}>Template Name</label>
                  <input className={input} value={d.name} onChange={e=>setD({name:e.target.value})} placeholder="e.g. Mining Division KPI Scorecard"/>
                </div>
                <div>
                  <label className={labelCls}>Visibility</label>
                  <div className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-xl bg-white">
                    <input type="checkbox" id="isShared" checked={d.isShared} onChange={e=>setD({isShared:e.target.checked})} className="rounded text-[#c8102e]"/>
                    <label htmlFor="isShared" className="text-sm text-slate-700 cursor-pointer">Share with all users</label>
                  </div>
                </div>
                <div className="md:col-span-3">
                  <label className={labelCls}>Description (optional)</label>
                  <input className={input} value={d.description} onChange={e=>setD({description:e.target.value})} placeholder="Brief description shown in the template picker"/>
                </div>
              </div>

              {/* Step 1: Data Source + Output Format */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 rounded-2xl p-4">
                <div>
                  <label className={labelCls}><Layers size={11} className="inline mr-1"/>Step 1 — Data Source</label>
                  <select className={input} value={d.dataSource}
                          onChange={e=>setD({ dataSource: e.target.value, fields: [], filters: {}, sortBy: '', groupBy: '' })}>
                    <option value="KPIs">KPIs (Performance Indicators)</option>
                    <option value="Risks">Risks (Enterprise Register)</option>
                    <option value="Projects">Projects (Portfolio)</option>
                    <option value="Grants">Grants (Funding)</option>
                    <option value="Divisions">Divisions</option>
                    <option value="Objectives">Strategic Objectives</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}><FileText size={11} className="inline mr-1"/>Step 2 — Output Format</label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {['PDF','Word','Excel'].map(f => (
                      <button key={f} onClick={()=>setD({outputFormat:f})}
                              className={`px-3 py-2 rounded-xl text-sm font-semibold transition-colors border ${
                                d.outputFormat===f ? 'bg-[#c8102e] text-white border-[#c8102e]'
                                                   : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                              }`}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Step 3: Field selection */}
              <div className="bg-slate-50 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className={labelCls + ' !mb-0'}><Settings2 size={11} className="inline mr-1"/>Step 3 — Fields to Include ({d.fields.length} selected)</label>
                  <div className="flex gap-1.5">
                    <button onClick={()=>setD({fields: catalogue.map(c=>c.key)})} className="text-[10px] text-[#8f1024] font-semibold hover:underline">Select all</button>
                    <span className="text-slate-300">·</span>
                    <button onClick={()=>setD({fields: []})} className="text-[10px] text-slate-500 font-semibold hover:underline">Clear</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-48 overflow-y-auto pr-2">
                  {catalogue.map(c => (
                    <label key={c.key} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs cursor-pointer border transition-colors ${
                      d.fields.includes(c.key) ? 'bg-[#fff3f5] border-[#c8102e]/30 text-slate-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}>
                      <input type="checkbox" checked={d.fields.includes(c.key)} onChange={()=>toggleField(c.key)} className="rounded text-[#c8102e]"/>
                      <span className="truncate">{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Step 4: Filters */}
              <div className="bg-slate-50 rounded-2xl p-4">
                <label className={labelCls}><Filter size={11} className="inline mr-1"/>Step 4 — Filters (Optional)</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {(d.dataSource === 'KPIs' || d.dataSource === 'Projects') && (
                    <div>
                      <label className="text-[10px] text-slate-500 mb-1 block">Division</label>
                      <select className={input + ' text-sm'} value={d.filters.divisionId || ''} onChange={e=>setFilter('divisionId', e.target.value || null)}>
                        <option value="">All divisions</option>
                        {divisions.map(div => <option key={div.id} value={div.id}>{div.name}</option>)}
                      </select>
                    </div>
                  )}
                  {d.dataSource === 'KPIs' && (
                    <div>
                      <label className="text-[10px] text-slate-500 mb-1 block">APP Indicator Only</label>
                      <select className={input + ' text-sm'} value={d.filters.isAppIndicator==null?'':String(d.filters.isAppIndicator)} onChange={e=>setFilter('isAppIndicator', e.target.value==='' ? null : e.target.value==='true')}>
                        <option value="">All KPIs</option>
                        <option value="true">APP Indicators only</option>
                        <option value="false">Non-APP only</option>
                      </select>
                    </div>
                  )}
                  {d.dataSource === 'Risks' && (
                    <>
                      <div>
                        <label className="text-[10px] text-slate-500 mb-1 block">Tier</label>
                        <select className={input + ' text-sm'} value={d.filters.tier||''} onChange={e=>setFilter('tier', e.target.value||null)}>
                          <option value="">All tiers</option><option>Strategic</option><option>Operational</option><option>Project</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 mb-1 block">Above Appetite</label>
                        <select className={input + ' text-sm'} value={d.filters.isAboveAppetite?'true':''} onChange={e=>setFilter('isAboveAppetite', e.target.value==='true')}>
                          <option value="">All risks</option>
                          <option value="true">Above appetite only</option>
                        </select>
                      </div>
                    </>
                  )}
                  {d.dataSource === 'Projects' && (
                    <>
                      <div>
                        <label className="text-[10px] text-slate-500 mb-1 block">Project Type</label>
                        <select className={input + ' text-sm'} value={d.filters.projectType||''} onChange={e=>setFilter('projectType', e.target.value||null)}>
                          <option value="">All types</option><option>Research</option><option>Commercial</option><option>Contract</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 mb-1 block">Status</label>
                        <select className={input + ' text-sm'} value={d.filters.status||''} onChange={e=>setFilter('status', e.target.value||null)}>
                          <option value="">All statuses</option><option>Draft</option><option>Active</option><option>InProgress</option><option>Completed</option><option>OnHold</option><option>Cancelled</option>
                        </select>
                      </div>
                    </>
                  )}
                  {/* V3.4 — Grants filters */}
                  {d.dataSource === 'Grants' && (
                    <>
                      <div>
                        <label className="text-[10px] text-slate-500 mb-1 block">Status</label>
                        <select className={input + ' text-sm'} value={d.filters.status||''} onChange={e=>setFilter('status', e.target.value||null)}>
                          <option value="">All statuses</option>
                          <option>Applied</option><option>Awarded</option><option>Active</option>
                          <option>Reporting</option><option>Closed</option><option>Rejected</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 mb-1 block">Funder</label>
                        <select className={input + ' text-sm'} value={d.filters.funder||''} onChange={e=>setFilter('funder', e.target.value||null)}>
                          <option value="">All funders</option>
                          {[...new Set(grants.map(g => g.funder).filter(Boolean))].sort().map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 mb-1 block">Programme</label>
                        <select className={input + ' text-sm'} value={d.filters.programme||''} onChange={e=>setFilter('programme', e.target.value||null)}>
                          <option value="">All programmes</option>
                          {[...new Set(grants.map(g => g.programme).filter(Boolean))].sort().map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Step 5: Sort + Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-50 rounded-2xl p-4">
                <div>
                  <label className={labelCls}>Sort by</label>
                  <select className={input + ' text-sm'} value={d.sortBy} onChange={e=>setD({sortBy:e.target.value})}>
                    <option value="">— No sort —</option>
                    {catalogue.map(c=><option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Sort order</label>
                  <select className={input + ' text-sm'} value={d.sortOrder} onChange={e=>setD({sortOrder:e.target.value})}>
                    <option value="asc">Ascending (A → Z, low → high)</option>
                    <option value="desc">Descending (Z → A, high → low)</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Document Options</label>
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={d.includeCoverPage} onChange={e=>setD({includeCoverPage:e.target.checked})} className="rounded text-[#c8102e]"/>
                      Include cover page
                    </label>
                    <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={d.includeExecSummary} onChange={e=>setD({includeExecSummary:e.target.checked})} className="rounded text-[#c8102e]"/>
                      Include executive summary
                    </label>
                  </div>
                </div>
              </div>

              {/* Preview & Validation */}
              <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 rounded-xl bg-[#fff7f8] border border-[#c8102e]/20">
                <div className="text-[11px] text-slate-700 flex items-center gap-2">
                  <Sparkles size={14} className="text-[#8f1024]"/>
                  <span><b>{previewCount}</b> rows would be included &middot; <b>{d.fields.length}</b> columns &middot; output: <b>{d.outputFormat}</b></span>
                </div>
                {(!d.name || d.fields.length === 0) && (
                  <span className="text-[10px] text-amber-700 font-semibold">
                    {!d.name ? 'Name is required to save.' : 'Select at least one field.'}
                  </span>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
                <button onClick={()=>{setBuilderOpen(false); setEditingTemplate(null); setDraftTemplate(null);}}
                        className="px-4 py-2 rounded-xl text-sm text-slate-600 hover:bg-slate-100 font-semibold">
                  Cancel
                </button>
                <div className="flex gap-2 flex-wrap justify-end">
                  <button
                    disabled={d.fields.length === 0}
                    onClick={()=>runCustomReport({
                      id: d.id, name: d.name || 'Untitled Report',
                      description: d.description, dataSource: d.dataSource,
                      outputFormat: d.outputFormat,
                      configurationJson: { fields: d.fields, filters: d.filters, groupBy: d.groupBy, sortBy: d.sortBy, sortOrder: d.sortOrder, includeCoverPage: d.includeCoverPage, includeExecSummary: d.includeExecSummary }
                    })}
                    className={btnS + ' disabled:opacity-50 disabled:cursor-not-allowed'}>
                    <Play size={12}/> Run Without Saving
                  </button>
                  <button
                    disabled={!d.name || d.fields.length === 0}
                    onClick={()=>saveTemplate({
                      id: d.id, name: d.name, description: d.description,
                      dataSource: d.dataSource, outputFormat: d.outputFormat,
                      isShared: d.isShared,
                      configurationJson: { fields: d.fields, filters: d.filters, groupBy: d.groupBy, sortBy: d.sortBy, sortOrder: d.sortOrder, includeCoverPage: d.includeCoverPage, includeExecSummary: d.includeExecSummary }
                    })}
                    className={btnP + ' disabled:opacity-50 disabled:cursor-not-allowed'}>
                    <Save size={12}/> {editingTemplate ? 'Save Changes' : 'Save Template'}
                  </button>
                  <button
                    disabled={!d.name || d.fields.length === 0}
                    onClick={async()=>{
                      await saveTemplate({
                        id: d.id, name: d.name, description: d.description,
                        dataSource: d.dataSource, outputFormat: d.outputFormat, isShared: d.isShared,
                        configurationJson: { fields: d.fields, filters: d.filters, groupBy: d.groupBy, sortBy: d.sortBy, sortOrder: d.sortOrder, includeCoverPage: d.includeCoverPage, includeExecSummary: d.includeExecSummary }
                      });
                      runCustomReport({
                        id: d.id, name: d.name, description: d.description,
                        dataSource: d.dataSource, outputFormat: d.outputFormat,
                        configurationJson: { fields: d.fields, filters: d.filters, groupBy: d.groupBy, sortBy: d.sortBy, sortOrder: d.sortOrder, includeCoverPage: d.includeCoverPage, includeExecSummary: d.includeExecSummary }
                      });
                    }}
                    className={`${btnP} bg-[#8f1024] hover:bg-[#760d1d] disabled:opacity-50 disabled:cursor-not-allowed`}>
                    <Sparkles size={12}/> Save & Run
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
